import React, { useState, useEffect, useMemo } from 'react';
import { useLayout } from '../../../context/LayoutContext';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Breadcrumbs,
  Link,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton as MuiIconButton,
  Autocomplete,
  FormControlLabel,
  Switch,
  Checkbox,
   RadioGroup,    // ADD THIS
  Radio         // ADD THIS
} from '@mui/material';
import { 
  ArrowBackIos,
  ArrowForwardIos,
  Today,
  FilterAlt,
  FilterAltOff,
  Info,
  CalendarToday,
  Add,
  Edit,
  Delete,
  AccessTime,
  Schedule,
  NavigateNext,
  Person,
  Group,
  Print,
  WatchLater,
  DragIndicator,
  LocalHospital,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  useGetMonthlyAvailabilitiesQuery,
  useCreateAvailabilityMutation,
  useUpdateAvailabilityMutation,
  useDeleteAvailabilityMutation,
} from '../../../slices/availabilityApiSlice';
import { useGetUsersQuery } from '../../../slices/usersApiSlice';
import { getLocalDateString } from '../../../utils/dateUtils';

// Shift time presets
const SHIFT_PRESETS = {
  'LD': { start: '08:00', end: '20:00', label: 'Long Day (8:00-20:00)' },
  'ND': { start: '20:00', end: '08:00', label: 'Night Duty (20:00-8:00)' },
  'AM': { start: '08:00', end: '16:00', label: 'Morning (8:00-16:00)' },
  'PM': { start: '14:00', end: '20:00', label: 'Afternoon (14:00-20:00)' },
  'TW': { start: '20:00', end: '00:00', label: 'Twilight (20:00-00:00)' },
  'ELD': { start: '07:30', end: '20:30', label: 'Extended Long Day (7:30-20:30)' },
  'ESCORT': { start: '08:00', end: '20:00', label: 'Escort (8:00-20:00)' },
};

// Unavailability reasons (from StaffAvailability)
const UNAVAILABILITY_REASONS = [
'Annual Leave',
'Adoptive Leave',
'Bereavement Leave',
'Carers Leave.',
'Certified Sick Leave',
'Force Majeure',
'Health and Safety Leave',
'Jury Leave',
'Maternity Leave',
'Other',
'Parents Leave',
'Parental Leave',
'Paternity Leave',
'Sick Leave',
'Study Leave / Mandatory Training',
];


// Color coding for different availability statuses
const AVAILABILITY_STATUS_COLORS = {
  pending: '#e3f2fd',
  approved: '#e8f5e8',
  rejected: '#ffebee',
  sickLeave: '#f44336',
  sickLeavePartial: '#ff9800',
  unavailable: '#d32f2f',
  unavailableOther: '#9c27b0',
};

// Add this style object near your other style definitions
const scrollableTableStyle = {
  maxHeight: '600px', // Adjust this height as needed
  overflowY: 'auto',
  border: '1px solid #ddd',
  borderRadius: '8px',
  position: 'relative',
};


// Add PrintStyles component at the top level
const PrintStyles = () => (
  <style>{`
    @media print {
      .no-print {
        display: none !important;
      }
      .print-page {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
      @-moz-document url-prefix() {
        .availability-card, .status-chip, .day-header, .staff-info {
          box-shadow: inset 0 0 0 1000px var(--print-color) !important;
        }
      }
      body {
        margin: 0 !important;
        padding: 0 !important;
        background: white !important;
      }
      .print-container {
        width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      .availability-text {
        color: #000 !important;
        font-weight: bold !important;
      }
      .print-text {
        font-size: 11pt !important;
        line-height: 1.4 !important;
      }
      @page {
        margin: 0.5in !important;
      }
      .calendar-grid {
        width: 100% !important;
        table-layout: fixed !important;
        border-collapse: collapse !important;
      }
      .day-row {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      .print-header {
        text-align: center !important;
        margin-bottom: 20px !important;
        border-bottom: 2px solid #000 !important;
        padding-bottom: 10px !important;
      }
      .availability-pending {
        background-color: #e3f2fd !important;
      }
      .availability-approved {
        background-color: #e8f5e8 !important;
      }
      .availability-rejected {
        background-color: #ffebee !important;
      }
      .availability-sick-leave {
        background-color: #f44336 !important;
      }
      .availability-sick-partial {
        background-color: #ff9800 !important;
      }
      .availability-unavailable {
        background-color: #d32f2f !important;
      }
      .availability-force-majeure {
        background-color: #9c27b0 !important;
      }

      /* Analysis specific styles */
      .stat-card {
        border: 1px solid #ddd !important;
        border-radius: 8px !important;
        padding: 15px !important;
        text-align: center !important;
        background-color: white !important;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
      }
      .breakdown-table {
        width: 100% !important;
        border-collapse: collapse !important;
        margin-bottom: 25px !important;
      }
      .breakdown-table th,
      .breakdown-table td {
        border: 1px solid #ddd !important;
        padding: 10px !important;
        text-align: left !important;
      }
      .breakdown-table th {
        background-color: #f5f5f5 !important;
        font-weight: bold !important;
      }
      .percentage-bar {
        background-color: #e0e0e0 !important;
        height: 8px !important;
        border-radius: 4px !important;
        margin-top: 5px !important;
        overflow: hidden !important;
      }
      .percentage-fill {
        height: 100% !important;
        background-color: #1976d2 !important;
        border-radius: 4px !important;
      }
    
    }
  `}</style>
);

const isAvailabilityUnavailable = (availability) => {
  return availability.isUnavailable || availability.isSickLeave;
};


// Helper function to format hours and minutes
const formatHoursMinutes = (totalMinutes) => {
  if (!totalMinutes || totalMinutes === 0) return '0h 0m';
  
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
};

// Helper function to format time for display
const formatTime = (dateTime) => {
  if (!dateTime) return '';
  const date = new Date(dateTime);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Helper function to format date for display
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

// Helper function to get days in month
const getDaysInMonth = (year, month) => {
  return new Date(year, month + 1, 0).getDate();
};

// Generate month names
const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Generate years (from current year - 1 to current year + 1)
const generateYears = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear - 1; i <= currentYear + 5; i++) {
    years.push(i);
  }
  return years;
};

const StaffAvailabilityView = () => {
  const { setFluid } = useLayout();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth());
  const [calendarDays, setCalendarDays] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingAvailability, setEditingAvailability] = useState(null);
  
  // Add these state variables near the top of your component
const [analysisDialogOpen, setAnalysisDialogOpen] = useState(false);
const [analysisType, setAnalysisType] = useState('selectedStaff'); // 'selectedStaff' or 'jobTitle'
const [selectedJobTitleForAnalysis, setSelectedJobTitleForAnalysis] = useState('');
const [analysisMonth, setAnalysisMonth] = useState(selectedMonth);
const [analysisYear, setAnalysisYear] = useState(selectedYear);
const [analysisLoading, setAnalysisLoading] = useState(false);

  // Add this state for selected days
  const [selectedDays, setSelectedDays] = useState({
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false
  });

  // Staff selection state for managers/admins
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [filteredStaffList, setFilteredStaffList] = useState([]);
  const [selectedJobTitleFilter, setSelectedJobTitleFilter] = useState('all');
  const [availableJobTitles, setAvailableJobTitles] = useState([]);
  
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    startDateTime: '',
    endDateTime: '',
    comments: '',
    categoryOfStaff: '',
    wardDepartmentArea: '',
    isUnavailable: false,
    unavailablePayHours: false,
    unavailabilityReason: '',
    unavailabilityType: 'Full Day',
    unavailabilityDetails: '',
    isSickLeave: false,
    sickLeaveType: 'Full Day',
    sicknessReason: ''
  });
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Shift preset selection
  const [selectedPreset, setSelectedPreset] = useState('');
  
  // Drag and drop state
  const [draggingAvailability, setDraggingAvailability] = useState(null);
  const [dragOverDay, setDragOverDay] = useState(null);
  
  // Fetch staff data
  const { data: staffData, isLoading: loadingStaff } = useGetUsersQuery();
  
  // Get the staff ID to query
  const getStaffIdToQuery = () => {
    if (userInfo?.isAdmin || userInfo?.isManager) {
      return selectedStaff ? selectedStaff._id : undefined;
    } else {
      return userInfo?._id;
    }
  };

  // Fetch monthly availabilities
  const { 
    data: availabilitiesData, 
    isLoading: loadingAvailabilities,
    refetch: refetchAvailabilities,
    error: availabilitiesError
  } = useGetMonthlyAvailabilitiesQuery({
    year: selectedYear,
    month: selectedMonth + 1,
    staffId: getStaffIdToQuery()
  }, {
    skip: !userInfo?._id || ((userInfo?.isAdmin || userInfo?.isManager) && !selectedStaff)
  });
  
  // Mutations
  const [createAvailability, { isLoading: creating }] = useCreateAvailabilityMutation();
  const [updateAvailability, { isLoading: updating }] = useUpdateAvailabilityMutation();
  const [deleteAvailability, { isLoading: deleting }] = useDeleteAvailabilityMutation();
  
  // Calculate month info
 const monthInfo = useMemo(() => {
  const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
  
  // Calculate the first day of the month
  const firstDayOfMonth = new Date(selectedYear, selectedMonth, 1);
  const firstDayOfWeek = firstDayOfMonth.getDay();
  
  // Calculate days from previous month to show
  let daysFromPreviousMonth;
  if (firstDayOfWeek === 0) {
    daysFromPreviousMonth = 6;
  } else {
    daysFromPreviousMonth = firstDayOfWeek - 1;
  }
  
  // Calculate the calendar start date (first Monday shown)
  const calendarStartDate = new Date(firstDayOfMonth);
  calendarStartDate.setDate(calendarStartDate.getDate() - daysFromPreviousMonth);
  
  // Calculate the calendar end date (last Sunday shown)
  //const totalCells = 35; // 5 weeks
  const totalCells = 42; // 6 rows of 7 days (always show 6 weeks)
  const calendarEndDate = new Date(calendarStartDate);
  calendarEndDate.setDate(calendarEndDate.getDate() + totalCells - 1);
  
  return {
    daysInMonth,
    monthName: monthNames[selectedMonth],
    year: selectedYear,
    calendarStartDate,
    calendarEndDate,
    daysFromPreviousMonth
  };
}, [selectedYear, selectedMonth]);

  // Helper functions
  const getDayNameFromDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const day = date.getDay();
    return ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][day];
  };

  const handleDayToggle = (day) => {
    setSelectedDays(prev => ({
      ...prev,
      [day]: !prev[day]
    }));
  };

  const submitSingleAvailability = async (startDate, endDate, isEditMode = false) => {
    const startDateTimeISO = startDate.toISOString();
    const endDateTimeISO = endDate.toISOString();
    
    // For managers/admins creating/editing availability for others
    if (userInfo.isAdmin || userInfo.isManager) {
      if (!formData.requestedBy) {
        throw new Error('Staff information is missing');
      }
      
      const submissionData = {
        ...formData,
        startDateTime: startDateTimeISO,
        endDateTime: endDateTimeISO,
        // Auto-approve unavailability if manager is creating
        ...(formData.isUnavailable && (userInfo.isAdmin || userInfo.isManager) && {
          status: 'Approved',
          approved: true
        }),
        createdByManager: (userInfo.isAdmin || userInfo.isManager) ? {
          id: userInfo._id,
          name: userInfo.name,
          email: userInfo.email,
          date: new Date().toISOString()
        } : undefined,
        // Ensure these fields are explicitly set
        requestedBy: formData.requestedBy,
        requestedByName: formData.requestedByName,
        requestedByEmail: formData.requestedByEmail
      };
      
      if (isEditMode && editingAvailability) {
        // For edit mode, we only update the original
        if (startDateTimeISO === editingAvailability.startDateTime) {
          return await updateAvailability({
            id: editingAvailability._id,
            ...submissionData
          }).unwrap();
        }
        // If it's a different date in edit mode, create new
        return await createAvailability(submissionData).unwrap();
      } else {
        // Create new availability
        return await createAvailability(submissionData).unwrap();
      }
    } else {
      // Regular users creating their own availability
      const submissionData = {
        ...formData,
        startDateTime: startDateTimeISO,
        endDateTime: endDateTimeISO,
        requestedBy: userInfo._id,
        requestedByName: userInfo.name,
        requestedByEmail: userInfo.email
      };
      
      if (isEditMode && editingAvailability) {
        // For edit mode, we only update the original
        if (startDateTimeISO === editingAvailability.startDateTime) {
          return await updateAvailability({
            id: editingAvailability._id,
            ...submissionData
          }).unwrap();
        }
        // If it's a different date in edit mode, create new
        return await createAvailability(submissionData).unwrap();
      } else {
        return await createAvailability(submissionData).unwrap();
      }
    }
  };


  // Add this function near your other utility functions
const fetchBulkAvailabilities = async (staffIds, year, month) => {
  try {
    const promises = staffIds.map(async (staffId) => {
      try {
        // Use the existing getMonthlyAvailabilities endpoint
        const response = await fetch(
          `/api/availability/monthly?year=${year}&month=${month}&staffId=${staffId}`
        );
        
        if (!response.ok) {
          throw new Error(`Failed to fetch for staff ${staffId}`);
        }
        
        const data = await response.json();
        return {
          staffId,
          data: data.availabilities || {},
          success: true
        };
      } catch (error) {
        console.error(`Error fetching for staff ${staffId}:`, error);
        return {
          staffId,
          data: {},
          success: false,
          error: error.message
        };
      }
    });
    
    const results = await Promise.allSettled(promises);
    return results.map(result => result.value || result.reason);
  } catch (error) {
    console.error('Error in bulk fetch:', error);
    throw error;
  }
};

  // Function to fetch availability data for multiple staff
const fetchMultipleStaffAvailabilities = async (staffIds, year, month) => {
  try {
    const availabilitiesPromises = staffIds.map(async (staffId) => {
      try {
        // You'll need to implement this API endpoint or use existing one
        const response = await fetch(`/api/availabilities/monthly?year=${year}&month=${month}&staffId=${staffId}`);
        const data = await response.json();
        return {
          staffId,
          data: data.availabilities || {}
        };
      } catch (error) {
        console.error(`Error fetching data for staff ${staffId}:`, error);
        return {
          staffId,
          data: {},
          error: true
        };
      }
    });
    
    const results = await Promise.all(availabilitiesPromises);
    return results;
  } catch (error) {
    console.error('Error fetching multiple staff availabilities:', error);
    throw error;
  }
};

  // Helper function to get the specific dates for selected days
const getSelectedDatesInfo = () => {
  if (!formData.startDateTime) return [];
  
  const originalDate = new Date(formData.startDateTime);
  const originalDayIndex = originalDate.getDay();
  const dayNameMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  
  return Object.entries(selectedDays)
    .filter(([_, isSelected]) => isSelected)
    .map(([dayKey]) => {
      const targetDayIndex = dayNameMap.indexOf(dayKey);
      let dayOffset;
      
      if (targetDayIndex >= originalDayIndex) {
        dayOffset = targetDayIndex - originalDayIndex;
      } else {
        dayOffset = (7 - originalDayIndex) + targetDayIndex;
      }
      
      const targetDate = new Date(originalDate);
      targetDate.setDate(targetDate.getDate() + dayOffset);
      
      return {
        dayName: dayKey.charAt(0).toUpperCase() + dayKey.slice(1),
        date: targetDate,
        isOriginalDay: dayKey === dayNameMap[originalDayIndex]
      };
    })
    .sort((a, b) => a.date - b.date); // Sort by date
};

  // Initialize selected staff based on user role
  useEffect(() => {
    if (userInfo) {
      if (userInfo.isManager || userInfo.isAdmin) {
        // Don't select anyone by default initially - wait for staff list to load
      } else {
        // Regular users see their own availability
        setSelectedStaff({
          _id: userInfo._id,
          name: userInfo.name,
          email: userInfo.email,
          jobtitle: userInfo.jobtitle,
          ward: userInfo.ward
        });
      }
    }
  }, [userInfo]);
  
  // Process staff data and set first staff as default for admins/managers
  useEffect(() => {
    if (staffData) {
      // Filter for staff members (non-admin, non-agency)
      const filteredStaff = staffData.filter(user => 
        !user.isAdmin && !user.isAgency && user.isActive !== false
      );
      
      setStaffList(filteredStaff);
      setFilteredStaffList(filteredStaff);
      
      // Extract unique job titles
      const jobTitles = [...new Set(filteredStaff
        .map(staff => staff.jobtitle)
        .filter(title => title && title.trim() !== '')
      )].sort();
      
      setAvailableJobTitles(['all', ...jobTitles]);
      
      // For admins/managers, select the first staff member by default
      if ((userInfo?.isManager || userInfo?.isAdmin) && filteredStaff.length > 0 && !selectedStaff) {
        const firstStaff = filteredStaff[0];
        setSelectedStaff({
          ...firstStaff,
          ward: firstStaff.ward || firstStaff.wardDepartmentArea || 'No Ward'
        });
      }
    }
  }, [staffData, userInfo, selectedStaff]);
  
  // Apply job title filter to staff list
  useEffect(() => {
    if (selectedJobTitleFilter === 'all') {
      setFilteredStaffList(staffList);
    } else {
      const filtered = staffList.filter(staff => 
        staff.jobtitle === selectedJobTitleFilter
      );
      setFilteredStaffList(filtered);
      
      // If selected staff is not in filtered list, select first staff in filtered list
      if (selectedStaff && !filtered.some(staff => staff._id === selectedStaff._id)) {
        if (filtered.length > 0) {
          const newStaff = filtered[0];
          setSelectedStaff({
            ...newStaff,
            ward: newStaff.ward || newStaff.wardDepartmentArea || 'No Ward'
          });
        } else {
          setSelectedStaff(null);
        }
      }
    }
  }, [selectedJobTitleFilter, staffList, selectedStaff]);
  
  // Generate calendar days
// Generate calendar days
// Generate calendar days
useEffect(() => {
  const daysArray = [];
  const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
  
  // Find the first day of the month
  const firstDayOfMonth = new Date(selectedYear, selectedMonth, 1);
  // Get day of week (0 = Sunday, 1 = Monday, ... 6 = Saturday)
  const firstDayOfWeek = firstDayOfMonth.getDay();
  
  // Calculate days from previous month to show
  // We want Monday (1) as first day of week, so we need to adjust
  let daysFromPreviousMonth;
  if (firstDayOfWeek === 0) {
    // If first day is Sunday, we need to show 6 days from previous month (Mon-Sat)
    daysFromPreviousMonth = 6;
  } else {
    // If first day is Monday (1), we show 0 days from previous month
    // If first day is Tuesday (2), we show 1 day from previous month, etc.
    daysFromPreviousMonth = firstDayOfWeek - 1;
  }
  
  // Always show 6 weeks for consistency
  const totalCells = 42; // 6 weeks * 7 days
  
  // Start from the first Monday before the month starts
  const startDate = new Date(firstDayOfMonth);
  startDate.setDate(startDate.getDate() - daysFromPreviousMonth);
  
  // Generate cells for the calendar grid
  for (let i = 0; i < totalCells; i++) {
    const currentDate = new Date(startDate);
    currentDate.setDate(startDate.getDate() + i);
    
    const dateString = getLocalDateString(currentDate);
    const dayOfWeek = currentDate.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    
    // Check if the date is in the current month
    const isInMonth = currentDate.getMonth() === selectedMonth && 
                     currentDate.getFullYear() === selectedYear;
    
    // Check if today
    const today = new Date();
    const todayDateString = getLocalDateString(today);
    const isToday = dateString === todayDateString;
    
    daysArray.push({
      dayNumber: currentDate.getDate(),
      date: dateString,
      isInMonth: isInMonth,
      isWeekend: isWeekend,
      isToday: isToday,
      dayName: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dayOfWeek],
      // Store the full date object for easier calculations
      dateObj: new Date(currentDate)
    });
  }
  
  setCalendarDays(daysArray);
}, [selectedYear, selectedMonth]);


  
  // Handle layout
  useEffect(() => {
    setFluid(true);
    return () => {
      setFluid(false);
    };
  }, [setFluid]);
  
  // Navigation functions
  const navigateMonth = (direction) => {
    let newYear = selectedYear;
    let newMonth = selectedMonth + direction;
    
    if (newMonth < 0) {
      newMonth = 11;
      newYear -= 1;
    } else if (newMonth > 11) {
      newMonth = 0;
      newYear += 1;
    }
    
    setSelectedYear(newYear);
    setSelectedMonth(newMonth);
  };
  
  const goToCurrentMonth = () => {
    const current = new Date();
    setSelectedYear(current.getFullYear());
    setSelectedMonth(current.getMonth());
  };
  
  // Handle shift preset selection
  const handlePresetChange = (presetKey) => {
  setSelectedPreset(presetKey);
  
  if (presetKey && SHIFT_PRESETS[presetKey]) {
    const preset = SHIFT_PRESETS[presetKey];
    
    if (selectedDay && selectedDay.date) {
      // Parse the date parts from selectedDay.date
      const [year, month, day] = selectedDay.date.split('-').map(Number);
      
      // Parse start time
      const [startHours, startMinutes] = preset.start.split(':').map(Number);
      
      // Create dates in UTC to avoid timezone issues
      //const startDateTime = new Date(Date.UTC(year, month - 1, day, startHours, startMinutes, 0));
      const startDateTime = new Date(year, month - 1, day, startHours, startMinutes, 0);

      // Parse end time
      const [endHours, endMinutes] = preset.end.split(':').map(Number);
      
      // Create end date
      //const endDateTime = new Date(Date.UTC(year, month - 1, day, endHours, endMinutes, 0));
      const endDateTime   = new Date(year, month - 1, day, endHours, endMinutes, 0);

      // Check if this shift crosses midnight (end time is earlier than start time)
      const crossesMidnight = endHours < startHours;
      
      // For any shift that crosses midnight, end time is next day
      //if (crossesMidnight) {
      //  endDateTime.setUTCDate(endDateTime.getUTCDate() + 1);
      //}

      if (crossesMidnight) {
      endDateTime.setDate(endDateTime.getDate() + 1);
      }

      
      // Convert to local ISO string for the datetime-local input
      // We need to adjust for timezone offset
      const formatForInput = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };
      
      setFormData(prev => ({
        ...prev,
        startDateTime: formatForInput(startDateTime),
        endDateTime: formatForInput(endDateTime)
      }));
    }
  }
};

  // Auto-set end date based on start date
  const handleStartDateChange = (e) => {
    const { value } = e.target;
    setFormData(prev => {
      // If end date is empty or before the new start date, set end date to 8 hours later
      const newStartDate = new Date(value);
      let newEndDate = new Date(value);
      
      // If end date is not set or is before/equal to start date, set it to 8 hours later
      if (!prev.endDateTime || new Date(prev.endDateTime) <= newStartDate) {
        newEndDate.setHours(newEndDate.getHours() + 8);
      } else {
        // Keep the same end date but ensure it's on the same day as start date
        const originalEndDate = new Date(prev.endDateTime);
        originalEndDate.setFullYear(
          newStartDate.getFullYear(),
          newStartDate.getMonth(),
          newStartDate.getDate()
        );
        newEndDate = originalEndDate;
      }
      
      return {
        ...prev,
        startDateTime: value,
        endDateTime: newEndDate.toISOString().slice(0, 16)
      };
    });
    
    // Clear preset if user manually changes time
    setSelectedPreset('');
  };

  const handleDayClick = (day) => {
  if (!day.isInMonth || !day.date) return;
  
  setSelectedDay(day);
  setSelectedPreset(''); // Reset preset when clicking new day
  
  // Parse the date parts
  const [year, month, dayNum] = day.date.split('-').map(Number);
  
  // Get the day name from the clicked date
  const dayName = getDayNameFromDate(day.date);
  
  // Reset all days and select only the clicked day
  const resetDays = {
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false
  };
  
  if (dayName) {
    resetDays[dayName] = true;
  }
  
  setSelectedDays(resetDays);
  
  // Create dates in UTC to avoid timezone issues
  const today = new Date();
  const todayUTC = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
  
  // Default start time: 09:00
  //const defaultStart = new Date(Date.UTC(year, month - 1, dayNum, 8, 0, 0));
  const defaultStart = new Date(year, month - 1, dayNum, 8, 0, 0);

  // Default end time: 17:00 (8 hours later)
  //const defaultEnd = new Date(Date.UTC(year, month - 1, dayNum, 16, 0, 0));  
  const defaultEnd   = new Date(year, month - 1, dayNum, 16, 0, 0);
  
  // If selected date is today and current time is after 09:00, use current time + 1 hour as start
  if (day.date === getLocalDateString(today.toISOString()) && today.getHours() >= 9) {
    defaultStart.setUTCHours(today.getHours(), today.getMinutes(), 0, 0);
    defaultEnd.setUTCHours(today.getHours() + 1, today.getMinutes(), 0, 0);
  }
  
  // Helper to format date for datetime-local input
  const formatForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };
  
  // Determine which staff to use (rest of the code remains the same)
  let staffToUse;
  
  if (userInfo.isAdmin || userInfo.isManager) {
    staffToUse = selectedStaff;
    if (!staffToUse) {
      showSnackbar('Please select a staff member first', 'warning');
      return;
    }
  } else {
    staffToUse = {
      _id: userInfo._id,
      name: userInfo.name,
      email: userInfo.email,
      jobtitle: userInfo.jobtitle,
      ward: userInfo.ward
    };
  }
  
  setFormData({
    startDateTime: formatForInput(defaultStart),
    endDateTime: formatForInput(defaultEnd),
    comments: '',
    categoryOfStaff: staffToUse.jobtitle || '',
    wardDepartmentArea: staffToUse.ward || '',
    isUnavailable: false,
    unavailablePayHours: false,
    unavailabilityReason: '',
    unavailabilityType: 'Full Day',
    unavailabilityDetails: '',
    isSickLeave: false,
    sickLeaveType: 'Full Day',
    sicknessReason: '',
    requestedBy: staffToUse._id,
    requestedByName: staffToUse.name,
    requestedByEmail: staffToUse.email,
    createdByManager: (userInfo.isAdmin || userInfo.isManager) ? {
      id: userInfo._id,
      name: userInfo.name,
      email: userInfo.email
    } : undefined
  });
  
  setEditMode(false);
  setEditingAvailability(null);
  setDialogOpen(true);
};
  
  const handleEditClick = (availability, event) => {
    event.stopPropagation();
    
    setEditingAvailability(availability);
    setEditMode(true);
    setSelectedPreset(''); // Reset preset when editing
    
    // Determine which staff info to use
    let staffToUse;
    
    if (userInfo.isAdmin || userInfo.isManager) {
      // Admin/manager editing - use the availability's original requestedBy info
      staffToUse = {
        _id: availability.requestedBy?._id || availability.requestedBy,
        name: availability.requestedByName,
        email: availability.requestedByEmail,
        jobtitle: availability.categoryOfStaff,
        ward: availability.wardDepartmentArea
      };
    } else {
      // Regular user editing their own
      staffToUse = {
        _id: userInfo._id,
        name: userInfo.name,
        email: userInfo.email,
        jobtitle: userInfo.jobtitle,
        ward: userInfo.ward
      };
    }
    
    // Format dates for datetime-local input (YYYY-MM-DDTHH:mm)
    const formatForInput = (dateString) => {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };
    
    setFormData({
      startDateTime: formatForInput(availability.startDateTime),
      endDateTime: formatForInput(availability.endDateTime),
      comments: availability.comments || '',
      categoryOfStaff: staffToUse.jobtitle || availability.categoryOfStaff || '',
      wardDepartmentArea: staffToUse.ward || availability.wardDepartmentArea || '',
      isUnavailable: availability.isUnavailable || availability.isSickLeave || false,
      unavailablePayHours: availability.unavailablePayHours || false,
      unavailabilityReason: availability.unavailabilityReason || availability.sicknessReason || '',
      unavailabilityType: availability.unavailabilityType || availability.sickLeaveType || 'Full Day',
      unavailabilityDetails: availability.unavailabilityDetails || '',
      isSickLeave: availability.isSickLeave || false,
      sickLeaveType: availability.sickLeaveType || 'Full Day',
      sicknessReason: availability.sicknessReason || '',
      requestedBy: staffToUse._id,
      requestedByName: staffToUse.name,
      requestedByEmail: staffToUse.email,
      createdByManager: availability.createdByManager
    });
    
    // Find the day this availability belongs to
    const availabilityDate = new Date(availability.startDateTime);
    const dateString = getLocalDateString(availabilityDate.toISOString());
    
    // Find the day in calendarDays
    const dayForAvailability = calendarDays.find(day => day.date === dateString);
    if (dayForAvailability) {
      setSelectedDay(dayForAvailability);
    }
    
    setDialogOpen(true);
  };

  // Function to handle delete confirmation
  const handleDeleteConfirmOpen = () => {
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirmClose = () => {
    setDeleteConfirmOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!editingAvailability) return;
    
    try {
      await updateAvailability({
        id: editingAvailability._id,
        isActive: false
      }).unwrap();
      
      showSnackbar('Availability removed successfully', 'success');
      refetchAvailabilities();
      
      // Close both dialogs
      setDeleteConfirmOpen(false);
      setDialogOpen(false);
      setEditMode(false);
      setEditingAvailability(null);
    } catch (error) {
      showSnackbar(error?.data?.message || 'Failed to remove availability', 'error');
      setDeleteConfirmOpen(false);
    }
  };

  // Soft delete function
  const handleDeleteClick = async (availabilityId, event = null) => {
    if (event) {
      event.stopPropagation();
    }
    
    if (window.confirm('Are you sure you want to remove this availability? This action can be reversed by an administrator.')) {
      try {
        await updateAvailability({
          id: availabilityId,
          isActive: false
        }).unwrap();
        
        showSnackbar('Availability removed successfully', 'success');
        refetchAvailabilities();
        
        // If we're in edit dialog, close it
        if (editMode) {
          setDialogOpen(false);
          setEditMode(false);
          setEditingAvailability(null);
        }
      } catch (error) {
        showSnackbar(error?.data?.message || 'Failed to remove availability', 'error');
      }
    }
  };
  
  const handleFormSubmit = async () => {
  try {
    // Validate form (keep existing validation)
    if (!formData.startDateTime || !formData.endDateTime) {
      showSnackbar('Start and end times are required', 'error');
      return;
    }
    
    // Parse dates correctly - ensure they're Date objects
    const startTime = new Date(formData.startDateTime);
    const endTime = new Date(formData.endDateTime);
    
    if (endTime <= startTime) {
      showSnackbar('End time must be after start time', 'error');
      return;
    }
    
    // Check if duration exceeds 24 hours
    const durationHours = (endTime - startTime) / (1000 * 60 * 60);
    if (durationHours > 24) {
      showSnackbar('Availability cannot exceed 24 hours', 'error');
      return;
    }
    
    // Get the selected days
    const selectedDayKeys = Object.entries(selectedDays)
      .filter(([_, isSelected]) => isSelected)
      .map(([dayKey]) => dayKey);
    
    // If no additional days selected, just use the original date
    if (selectedDayKeys.length === 0) {
      await submitSingleAvailability(startTime, endTime, editMode);
      showSnackbar('Availability saved successfully', 'success');
      
      // Close dialog and refresh data
      setDialogOpen(false);
      refetchAvailabilities();
      setSelectedPreset('');
      return;
    }
    
    // Get the day of the week from the original start date
    const originalStartDate = new Date(formData.startDateTime);
    const originalDayIndex = originalStartDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const dayNameMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const originalDayName = dayNameMap[originalDayIndex];
    
    // Create an array to hold all promises
    const promises = [];
    const successMessages = [];
    
    // Add the original day if it's selected
    if (selectedDays[originalDayName]) {
      promises.push(
        submitSingleAvailability(startTime, endTime, editMode)
          .then(() => {
            successMessages.push(`${originalDayName.charAt(0).toUpperCase() + originalDayName.slice(1)}: Saved`);
          })
      );
    }
    
    // For each additional selected day, calculate the date and submit
    for (const dayKey of selectedDayKeys) {
      if (dayKey === originalDayName) continue; // Skip original day, already handled
      
      // Calculate the offset for this day - FIXED LOGIC
      const targetDayIndex = dayNameMap.indexOf(dayKey);
      let dayOffset;
      
      if (targetDayIndex >= originalDayIndex) {
        // If target day is later in the same week (e.g., Monday to Wednesday)
        dayOffset = targetDayIndex - originalDayIndex;
      } else {
        // If target day is earlier in the week (e.g., Monday to Sunday)
        // We need to go to the NEXT week's Sunday
        dayOffset = (7 - originalDayIndex) + targetDayIndex;
      }
      
      // Create new dates with the offset
      const newStartDate = new Date(startTime);
      newStartDate.setDate(newStartDate.getDate() + dayOffset);
      
      const newEndDate = new Date(endTime);
      newEndDate.setDate(newEndDate.getDate() + dayOffset);
      
      promises.push(
        submitSingleAvailability(newStartDate, newEndDate, false)
          .then(() => {
            successMessages.push(`${dayKey.charAt(0).toUpperCase() + dayKey.slice(1)}: Saved`);
          })
      );
    }
    
    // Execute all promises
    await Promise.all(promises);
    
    // Replace the success message in handleFormSubmit:
// Instead of just showing count, show which days were saved
const savedDaysInfo = getSelectedDatesInfo();
const savedDaysText = savedDaysInfo
  .map(info => `${info.dayName} ${info.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`)
  .join(', ');

showSnackbar(`Availability saved for: ${savedDaysText}`, 'success');
    
    // Close dialog and refresh data
    setDialogOpen(false);
    refetchAvailabilities();
    setSelectedPreset(''); // Reset preset after submission
    
  } catch (error) {
    console.error('Form submit error:', error);
    showSnackbar(error?.data?.message || 'Failed to save availability', 'error');
  }
};
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setEditingAvailability(null);
    setSelectedPreset('');
    // Reset selected days
    setSelectedDays({
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false
    });
  };
  
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'startDateTime') {
      handleStartDateChange(e);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  // Handle end date change separately
  const handleEndDateChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      endDateTime: value
    }));
    // Clear preset if user manually changes time
    setSelectedPreset('');
  };
  
  // Handle staff selection change for managers/admins
  const handleStaffChange = (event, newValue) => {
    setSelectedStaff(newValue);
    
    // Clear any existing availabilities when changing staff
    if (availabilitiesData) {
      refetchAvailabilities();
    }
  };
  
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };
  
  const clearFilters = () => {
    setSelectedJobTitleFilter('all');
    if (userInfo.isManager || userInfo.isAdmin) {
      setSelectedStaff(null);
    }
  };
  
  // Get availabilities for a specific day
  const getDayAvailabilities = (date) => {
    if (!availabilitiesData?.availabilities) {
      return [];
    }
    
    if ((userInfo?.isAdmin || userInfo?.isManager) && !selectedStaff) {
      return [];
    }
    
    // Try multiple date formats to match what the backend returns
    const dateObj = new Date(date);
    
    // Format 1: YYYY-MM-DD (most common)
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const formattedDate1 = `${year}-${month}-${day}`;
    
    // Format 2: YYYY-M-D (no padding)
    const formattedDate2 = `${year}-${dateObj.getMonth() + 1}-${dateObj.getDate()}`;
    
    // Format 3: ISO date string
    const formattedDate3 = dateObj.toISOString().split('T')[0];
    
    // Try each format
    const dayAvailabilities = 
      availabilitiesData.availabilities[formattedDate1] ||
      availabilitiesData.availabilities[formattedDate2] ||
      availabilitiesData.availabilities[formattedDate3] ||
      [];
    
    return dayAvailabilities;
  };
  
  // Get current viewing staff name
  const getViewingStaffName = () => {
    if (selectedStaff) {
      return selectedStaff.name;
    } else if (userInfo && !userInfo.isManager && !userInfo.isAdmin) {
      return userInfo.name;
    }
    return 'Select a staff member';
  };

  // Drag and drop handlers
  const handleDragStart = (availability, event) => {
    setDraggingAvailability(availability);
    event.dataTransfer.setData('text/plain', JSON.stringify(availability));
    event.dataTransfer.effectAllowed = 'move';
    
    // Add visual feedback
    event.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (event) => {
    setDraggingAvailability(null);
    setDragOverDay(null);
    
    // Remove visual feedback
    event.currentTarget.style.opacity = '1';
  };

  const handleDragOver = (day, event) => {
    event.preventDefault();
    if (day.isInMonth && day.date) {
      setDragOverDay(day);
    }
  };

  const handleDragLeave = () => {
    setDragOverDay(null);
  };

  const handleDrop = async (day, event) => {
    event.preventDefault();
    setDragOverDay(null);
    
    if (!day.isInMonth || !day.date || !draggingAvailability) {
      return;
    }

    // Get the dragged availability data
    let availabilityData;
    try {
      const data = event.dataTransfer.getData('text/plain');
      availabilityData = JSON.parse(data);
    } catch (error) {
      console.error('Error parsing drag data:', error);
      return;
    }

    try {
      // Instead of trying to automatically move, let's open the edit dialog
      // with pre-filled dates for the new day
      setSelectedDay(day);
      setEditingAvailability(availabilityData);
      setEditMode(true);
      
      // Parse original dates
      const originalStart = new Date(availabilityData.startDateTime);
      const originalEnd = new Date(availabilityData.endDateTime);
      
      // Extract just the time (hours and minutes)
      const originalStartTime = {
        hours: originalStart.getHours(),
        minutes: originalStart.getMinutes()
      };
      
      const originalEndTime = {
        hours: originalEnd.getHours(),
        minutes: originalEnd.getMinutes()
      };
      
      // Create new dates with the target day and original times
      const targetDate = new Date(day.date);
      
      const newStartDate = new Date(targetDate);
      newStartDate.setHours(originalStartTime.hours, originalStartTime.minutes, 0, 0);
      
      const newEndDate = new Date(targetDate);
      newEndDate.setHours(originalEndTime.hours, originalEndTime.minutes, 0, 0);
      
      // Handle overnight shifts - if original end date is different from start date
      const originalStartDateStr = originalStart.toISOString().split('T')[0];
      const originalEndDateStr = originalEnd.toISOString().split('T')[0];
      
      if (originalEndDateStr !== originalStartDateStr) {
        // Original was overnight, add 1 day to end
        newEndDate.setDate(newEndDate.getDate() + 1);
      }
      
      // Convert to datetime-local format for the form
      const formatForForm = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };
      
      // Determine which staff info to use
      let staffToUse;
      
      if (userInfo.isAdmin || userInfo.isManager) {
        staffToUse = {
          _id: availabilityData.requestedBy?._id || availabilityData.requestedBy,
          name: availabilityData.requestedByName,
          email: availabilityData.requestedByEmail,
          jobtitle: availabilityData.categoryOfStaff,
          ward: availabilityData.wardDepartmentArea
        };
      } else {
        staffToUse = {
          _id: userInfo._id,
          name: userInfo.name,
          email: userInfo.email,
          jobtitle: userInfo.jobtitle,
          ward: userInfo.ward
        };
      }
      
      // Set form data with new dates
      setFormData({
        startDateTime: formatForForm(newStartDate),
        endDateTime: formatForForm(newEndDate),
        comments: availabilityData.comments || '',
        categoryOfStaff: staffToUse.jobtitle || availabilityData.categoryOfStaff || '',
        wardDepartmentArea: staffToUse.ward || availabilityData.wardDepartmentArea || '',
        requestedBy: staffToUse._id,
        requestedByName: staffToUse.name,
        requestedByEmail: staffToUse.email,
        createdByManager: availabilityData.createdByManager
      });
      
      // Show the dialog so user can review and save
      setDialogOpen(true);
      
      showSnackbar('Edit availability with new date. Please review and save.', 'info');
      
    } catch (error) {
      console.error('Error preparing move:', error);
      showSnackbar('Error preparing availability move', 'error');
    } finally {
      setDraggingAvailability(null);
    }
  };
  
  // Format day display
  const formatDayDisplay = (day) => {

    if (!day.isInMonth) {
    return null; // Don't show content for days not in month
  }
    
    const dayAvailabilities = getDayAvailabilities(day.date);
    const hasAvailabilities = dayAvailabilities.length > 0;
    
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 0.5,
          p: 0.5,
          bgcolor: day.isWeekend ? 'rgba(255, 152, 0, 0.3)' : 
                  day.isToday ? 'rgba(111, 153, 245, 0.3)' : 'rgba(99, 100, 102, .3)',
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Typography variant="caption" fontWeight="bold">
            {day.dayNumber} {day.dayName}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {day.isToday && (
              <Chip 
                label="Today" 
                size="small" 
                sx={{ 
                  height: 16, 
                  fontSize: '0.6rem',
                  bgcolor: 'info.light',
                  color: 'info.contrastText'
                }} 
              />
            )}
            {day.isWeekend && (
              <Chip 
                label="WE" 
                size="small" 
                sx={{ 
                  height: 16, 
                  fontSize: '0.6rem',
                  bgcolor: 'warning.light',
                  color: 'warning.contrastText'
                }} 
              />
            )}
          </Box>
        </Box>
        
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5, p: 0.5 , bgcolor: 'rgba(238, 238, 165, 0.33)' }}>
          {hasAvailabilities ? (
            <Box sx={{ maxHeight: '80px', overflow: 'auto' }}>
              {dayAvailabilities.map((availability) => (
                <Paper
                  key={availability._id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick(availability, e);
                  }}
                  sx={{ 
                    p: 0.5, 
                    mb: 0.5,
                    borderRadius: 0.5,
                    bgcolor: (availability.isUnavailable || availability.isSickLeave) ? 'error.light' :
                            availability.status === 'Approved' ? 'success.light' :
                            availability.status === 'Rejected' ? 'error.light' : 'info.light',
                    color: (availability.isUnavailable || availability.isSickLeave) ? 'error.contrastText' :
                          availability.status === 'Approved' ? 'success.contrastText' :
                          availability.status === 'Rejected' ? 'error.contrastText' : 'info.contrastText',
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: 2,
                      transform: 'translateY(-1px)',
                      transition: 'all 0.2s'
                    },
                    borderLeft: (availability.isUnavailable || availability.isSickLeave) ? '3px solid #d32f2f' : 'none'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {(availability.isUnavailable || availability.isSickLeave) ? (
                      <AccessTime fontSize="small" sx={{ color: '#d32f2f' }} />
                    ) : (
                      <AccessTime fontSize="small" sx={{ opacity: 0.7 }} />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block' }}>
                        {formatTime(availability.startDateTime)} - {formatTime(availability.endDateTime)}
                        {(availability.isUnavailable || availability.isSickLeave) && (
                          <Chip 
                            label="Unavailable" 
                            size="small" 
                            sx={{ 
                              ml: 1,
                              height: 14,
                              fontSize: '0.55rem',
                              bgcolor: '#d32f2f',
                              color: 'white'
                            }} 
                          />
                        )}
                        {availability.unavailablePayHours && (
                          <Chip 
                            label="For Pay" 
                            size="small" 
                            sx={{ 
                              ml: 0.5,
                              height: 14,
                              fontSize: '0.55rem',
                              bgcolor: '#caf3bd',
                              color: 'black'
                            }} 
                          />


                        )}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', opacity: 0.9 }}>
                        {formatHoursMinutes(availability.durationMinutes)}
                        {(availability.unavailabilityReason || availability.sicknessReason) && (
                          <span style={{ marginLeft: '4px', fontStyle: 'italic' }}>
                            • {availability.unavailabilityReason || availability.sicknessReason}
                          </span>
                        )}
                        {availability.createdByManager && !(availability.isUnavailable || availability.isSickLeave) && (
                          <span style={{ marginLeft: '4px', fontStyle: 'italic' }}>
                            • Added by manager
                          </span>
                        )}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <MuiIconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(availability, e);
                        }}
                        sx={{ 
                          color: 'inherit',
                          p: 0.25,
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' }
                        }}
                      >
                        <Edit fontSize="small" />
                      </MuiIconButton>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : (
            <Box sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 1
            }}>
              <Typography variant="caption" color="text.disabled" sx={{ textAlign: 'center' }}>
                No availability set
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', textAlign: 'center' }}>
                Click to add
              </Typography>
            </Box>
          )}
          
          {hasAvailabilities && (
            <Box sx={{ 
              mt: 'auto',
              pt: 0.5,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                Total
              </Typography>
              <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.65rem' }}>
                {formatHoursMinutes(dayAvailabilities.reduce((sum, a) => sum + a.durationMinutes, 0))}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  // Print function
  const handlePrint = () => {
    // Create a printable version of the availability calendar
    const printWindow = window.open('', '_blank');
    
    if (!printWindow) {
      showSnackbar('Please allow pop-ups to print', 'warning');
      return;
    }
    
    // Get viewing staff info
    const viewingStaff = selectedStaff || (userInfo && !userInfo.isManager && !userInfo.isAdmin ? {
      name: userInfo.name,
      jobtitle: userInfo.jobtitle,
      ward: userInfo.ward,
      id: userInfo._id
    } : null);
    
    // Prepare availability data for printing
    const availabilityMap = {};
    
    // Process availability data
    if (availabilitiesData?.availabilities) {
      Object.entries(availabilitiesData.availabilities).forEach(([dateString, availabilities]) => {
        if (availabilities.length > 0) {
          availabilityMap[dateString] = availabilities.map(avail => ({
            _id: avail._id,
            startDateTime: avail.startDateTime,
            endDateTime: avail.endDateTime,
            durationMinutes: avail.durationMinutes,
            status: avail.status || 'Pending',
            isUnavailable: avail.isUnavailable || false,
            unavailablePayHours: avail.unavailablePayHours || false, // ADD THIS LINE
            isSickLeave: avail.isSickLeave || false,
            unavailabilityReason: avail.unavailabilityReason || '',
            sicknessReason: avail.sicknessReason || '',
            unavailabilityType: avail.unavailabilityType || '',
            sickLeaveType: avail.sickLeaveType || '',
            comments: avail.comments || '',
            createdByManager: avail.createdByManager
          }));
        }
      });
    }
    
    // Build the printable HTML with colors
    let printHTML = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Staff Availability - ${monthInfo.monthName} ${monthInfo.year}</title>
        <style>
          @page {
            size: landscape;
            margin: 0.5in;
          }
          
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            background: white;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
            font-size: 10px;
          }
          
          .print-header {
            text-align: center;
            margin-bottom: 20px;
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
          }
          
          .print-title {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 5px;
          }
          
          .print-subtitle {
            font-size: 16px;
            color: #666;
            margin-bottom: 10px;
          }
          
          .print-info {
            font-size: 12px;
            color: #666;
            margin-bottom: 5px;
          }
          
          .calendar-grid {
            display: grid;
            grid-template-columns: repeat(7, 1fr);
            gap: 8px;
            margin-top: 20px;
          }
          
          .day-cell {
            border: 1px solid #ddd;
            border-radius: 4px;
            padding: 8px;
            min-height: 150px;
            background-color: white;
            position: relative;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          
          .day-header {
            font-weight: bold;
            padding-bottom: 5px;
            margin-bottom: 5px;
            border-bottom: 1px solid #eee;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          
          .day-number {
            background-color: #1976d2;
            color: white;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
          }
          
          .weekend-cell {
            background-color: #fff8e1 !important;
            border: 1px solid #ffcc80 !important;
          }
          
          .today-cell {
            background-color: #e3f2fd !important;
            border: 2px solid #1976d2 !important;
          }
          
          /* AVAILABILITY CARDS - KEEP COLORS */
          .availability-card {
            padding: 4px;
            margin: 2px 0;
            border-radius: 4px;
            font-size: 9px;
            color: white !important;
            font-weight: bold;
            page-break-inside: avoid;
            break-inside: avoid;
            min-height: 40px;
            border: 1px solid rgba(0,0,0,0.1);
          }
          
          .availability-pending {
            background-color: #2196f3 !important;
            border-left: 3px solid #1976d2 !important;
          }
          
          .availability-approved {
            background-color: #4caf50 !important;
            border-left: 3px solid #2e7d32 !important;
          }
          
          .availability-rejected {
            background-color: #f44336 !important;
            border-left: 3px solid #d32f2f !important;
          }
          
          /* UNAVAILABILITY CARDS - PROMINENT */
          .unavailable-card {
            background-color: #d32f2f !important;
            border-left: 3px solid #b71c1c !important;
            color: white !important;
            border: 2px solid #b71c1c !important;
          }
          
          .sick-leave-card {
            background-color: #f44336 !important;
            border-left: 3px solid #d32f2f !important;
            color: white !important;
            border: 2px solid #d32f2f !important;
          }
          
          .sick-partial-card {
            background-color: #ff9800 !important;
            border-left: 3px solid #e65100 !important;
            color: white !important;
            border: 2px solid #e65100 !important;
          }
          
          .force-majeure-card {
            background-color: #9c27b0 !important;
            border-left: 3px solid #7b1fa2 !important;
            color: white !important;
            border: 2px solid #7b1fa2 !important;
          }
          
          .availability-time {
            font-weight: bold;
            font-size: 9px;
            margin-bottom: 2px;
          }
          
          .unavailable-label {
            font-size: 8px;
            font-weight: bold;
            margin-bottom: 2px;
            text-transform: uppercase;
          }
          
          .unavailable-reason {
            font-size: 7px;
            margin-top: 2px;
            font-style: italic;
            opacity: 0.9;
          }
          
          .availability-details {
            font-size: 7px;
            margin-top: 2px;
            opacity: 0.9;
          }
          
          .empty-day {
            text-align: center;
            color: #999;
            font-style: italic;
            font-size: 9px;
            padding: 20px 5px;
          }
          
          .day-total {
            position: absolute;
            bottom: 5px;
            right: 5px;
            font-size: 9px;
            color: #666;
            background: rgba(255,255,255,0.8);
            padding: 2px 5px;
            border-radius: 3px;
          }
          
          .staff-info {
            padding: 15px;
            background-color: #f5f5f5;
            border-radius: 5px;
            margin: 15px auto;
            max-width: 600px;
            border: 1px solid #1976d2;
          }
          
          .summary {
            margin-top: 30px;
            padding: 15px;
            background-color: #e8f5e9;
            border: 1px solid #4caf50;
            border-radius: 5px;
            page-break-before: always;
          }
          
          .legend {
            margin-top: 20px;
            padding: 15px;
            border: 1px solid #ddd;
            background-color: #f9f9f9;
            border-radius: 5px;
          }
          
          .legend-item {
            display: flex;
            align-items: center;
            margin-bottom: 8px;
          }
          
          .legend-color {
            display: inline-block;
            width: 16px;
            height: 16px;
            margin-right: 8px;
            border-radius: 3px;
          }
          
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="print-header">
          <div class="print-title">Staff Availability Calendar</div>
          <div class="print-subtitle">${monthInfo.monthName} ${monthInfo.year}</div>
          <div class="print-info">Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
          <div class="print-info">Generated By: ${userInfo?.name || 'Manager'}</div>
        </div>
        
        ${viewingStaff ? `
          <div class="staff-info">
            <div><strong>Staff Name:</strong> ${viewingStaff.name}</div>
            ${viewingStaff.jobtitle ? `<div><strong>Position:</strong> ${viewingStaff.jobtitle}</div>` : ''}
            ${viewingStaff.ward ? `<div><strong>Ward/Department:</strong> ${viewingStaff.ward}</div>` : ''}
            <div><strong>Report Period:</strong> ${monthInfo.monthName} ${monthInfo.year}</div>
            ${viewingStaff.id ? `<div><strong>Staff ID:</strong> ${viewingStaff.id}</div>` : ''}
          </div>
        ` : ''}
        
        <div class="calendar-grid">
    `;
    
    // Add each day to the calendar
    calendarDays.forEach(day => {
      if (!day.isInMonth) {
        printHTML += '<div class="day-cell"></div>';
        return;
      }
      
      let dayClass = 'day-cell';
      if (day.isWeekend) dayClass += ' weekend-cell';
      if (day.isToday) dayClass += ' today-cell';
      
      printHTML += `
        <div class="${dayClass}">
          <div class="day-header">
            <div>
              <div style="font-size: 11px; font-weight: bold;">${day.dayName}</div>
              <div class="day-number">${day.dayNumber}</div>
            </div>
            ${day.isToday ? '<span style="font-size:9px;color:#1976d2;font-weight:bold;">TODAY</span>' : ''}
          </div>
      `;
      
      // Check for availabilities for this day
      let dayAvailabilities = [];
      
      if (day.date) {
        // Create date object from day.date
        const dateObj = new Date(day.date);
        
        // Try to match the date in YYYY-MM-DD format
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const dayNum = String(dateObj.getDate()).padStart(2, '0');
        const dateKey = `${year}-${month}-${dayNum}`;
        
        // Check for exact match first
        if (availabilityMap[dateKey]) {
          dayAvailabilities = availabilityMap[dateKey];
        } else {
          // Try other possible formats
          const alternativeKey1 = `${year}-${parseInt(month)}-${parseInt(dayNum)}`;
          const alternativeKey2 = day.date.split('T')[0];
          
          if (availabilityMap[alternativeKey1]) {
            dayAvailabilities = availabilityMap[alternativeKey1];
          } else if (availabilityMap[alternativeKey2]) {
            dayAvailabilities = availabilityMap[alternativeKey2];
          }
        }
      }
      
      if (dayAvailabilities.length > 0) {
        dayAvailabilities.forEach(avail => {
    // Determine the card class based on availability type
    let cardClass = 'availability-card ';
    
    if (avail.isSickLeave) {
      if (avail.sickLeaveType === 'Partial Day' || avail.sickLeaveType === 'Half Day AM' || avail.sickLeaveType === 'Half Day PM') {
        cardClass += 'sick-partial-card';
      } else {
        cardClass += 'sick-leave-card';
      }
    } else if (avail.isUnavailable) {
      if (avail.unavailabilityReason?.toLowerCase().includes('force majeure')) {
        cardClass += 'force-majeure-card';
      } else {
        cardClass += 'unavailable-card';
      }
    } else {
      // Regular availability
      if (avail.status === 'Approved') cardClass += 'availability-approved';
      else if (avail.status === 'Rejected') cardClass += 'availability-rejected';
      else cardClass += 'availability-pending';
    }
    
    const startTime = formatTime(avail.startDateTime);
    const endTime = formatTime(avail.endDateTime);
    const duration = formatHoursMinutes(avail.durationMinutes);
    const reason = avail.isSickLeave ? avail.sicknessReason : avail.unavailabilityReason;
    const type = avail.isSickLeave ? avail.sickLeaveType : avail.unavailabilityType;
    
    // FIXED: Add the unavailablePayHours check here
    printHTML += `
      <div class="${cardClass}">
        <div class="availability-time">${startTime} - ${endTime}</div>
        ${(avail.isUnavailable || avail.isSickLeave) ? `
          <div class="unavailable-label">
            ⛔ ${avail.isSickLeave ? 'SICK LEAVE' : 'UNAVAILABLE'}
            ${avail.unavailablePayHours ? '• FOR PAY' : ''}
          </div>
          <div class="unavailable-reason">${type}</div>
          ${reason ? `<div class="unavailable-reason">${reason}</div>` : ''}
        ` : `
          <div class="availability-details">Available • ${duration}</div>
          <div class="availability-details">Status: ${avail.status}</div>
        `}
        ${avail.comments ? `<div class="availability-details">Note: ${avail.comments}</div>` : ''}
      </div>
    `;
  });
        
        // Add day total
        const totalMinutes = dayAvailabilities.reduce((sum, a) => sum + a.durationMinutes, 0);
        printHTML += `<div class="day-total">Total: ${formatHoursMinutes(totalMinutes)}</div>`;
      } else {
        printHTML += `
          <div class="empty-day">
            No availability scheduled
          </div>
        `;
      }
      
      printHTML += '</div>';
    });
    
    // Calculate totals
    const totalAvailabilities = Object.values(availabilityMap).reduce((total, avails) => total + avails.length, 0);
    const totalHours = Object.values(availabilityMap).flat().reduce((sum, a) => sum + a.durationMinutes, 0);
    const unavailableCount = Object.values(availabilityMap).flat().filter(a => a.isUnavailable || a.isSickLeave).length;
    
    printHTML += `
        </div>
        
        <div class="summary">
          <div style="font-size: 14px; font-weight: bold; margin-bottom: 10px;">Monthly Summary</div>
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px;">
            <div>
              <strong>Total Available Shifts:</strong> ${totalAvailabilities}
            </div>
            <div>
              <strong>Total Available Hours:</strong> ${formatHoursMinutes(totalHours)}
            </div>
            <div>
              <strong>Unavailable Periods:</strong> ${unavailableCount}
            </div>
            <div>
              <strong>Dates with Availability:</strong> ${Object.keys(availabilityMap).length}
            </div>
            <div>
              <strong>Month:</strong> ${monthInfo.monthName} ${monthInfo.year}
            </div>
            <div>
              <strong>Staff:</strong> ${viewingStaff?.name || 'Selected staff'}
            </div>
          </div>
        </div>
        
        <div class="legend">
          <div style="font-size: 14px; font-weight: bold; margin-bottom: 10px;">Legend</div>
          <div class="legend-item">
            <span class="legend-color" style="background-color:#2196f3;border:1px solid #1976d2;"></span>
            <span>Pending Availability</span>
          </div>
          <div class="legend-item">
            <span class="legend-color" style="background-color:#4caf50;border:1px solid #2e7d32;"></span>
            <span>Approved Availability</span>
          </div>
          <div class="legend-item">
            <span class="legend-color" style="background-color:#f44336;border:1px solid #d32f2f;"></span>
            <span>Rejected Availability</span>
          </div>
          <div class="legend-item">
            <span class="legend-color" style="background-color:#d32f2f;border:1px solid #b71c1c;"></span>
            <span>Unavailable (General)</span>
          </div>
          <div class="legend-item">
            <span class="legend-color" style="background-color:#f44336;border:1px solid #d32f2f;"></span>
            <span>Sick Leave (Full Day)</span>
          </div>
          <div class="legend-item">
            <span class="legend-color" style="background-color:#ff9800;border:1px solid #e65100;"></span>
            <span>Sick Leave (Partial)</span>
          </div>
          <div class="legend-item">
            <span class="legend-color" style="background-color:#9c27b0;border:1px solid #7b1fa2;"></span>
            <span>Force Majeure / Special</span>
          </div>

          
          <div class="legend-item">
            <span style="font-size: 11px; font-weight: bold; color: #d32f2f;">⛔ FOR PAY</span>
            <span style="margin-left: 8px;">Unavailable hours included in pay calculation</span>
          </div>


          <div class="legend-item">
            <span class="legend-color" style="background-color:#fff8e1;border:1px solid #ffcc80;"></span>
            <span>Weekend Day</span>
          </div>
          <div class="legend-item">
            <span class="legend-color" style="background-color:#e3f2fd;border:2px solid #1976d2;"></span>
            <span>Today</span>
          </div>
        </div>
        
        <div style="margin-top:30px;padding-top:15px;border-top:1px solid #ddd;font-size:11px;color:#666;text-align:center;">
          Beaumont Hospital Staff Availability System<br>
          Generated on ${new Date().toLocaleDateString()} - For questions, contact: beaumontNursingBank@beaumont.ie
        </div>
        
        <script>
          window.onload = function() {
            // Print after a short delay
            setTimeout(() => {
              window.print();
              setTimeout(() => {
                window.close();
              }, 100);
            }, 500);
          };
          
          // Fallback in case onload doesn't fire
          setTimeout(function() {
            if (document.readyState === 'complete') {
              window.print();
              setTimeout(function() {
                window.close();
              }, 100);
            }
          }, 500);
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.write(printHTML);
    printWindow.document.close();
  };

// Print Analysis function
const handlePrintAnalysisOLD = () => {
  // Create a printable version of the availability analysis
  const printWindow = window.open('', '_blank');
  
  if (!printWindow) {
    showSnackbar('Please allow pop-ups to print', 'warning');
    return;
  }
  
  // Get viewing staff info
  const viewingStaff = selectedStaff || (userInfo && !userInfo.isManager && !userInfo.isAdmin ? {
    name: userInfo.name,
    jobtitle: userInfo.jobtitle,
    ward: userInfo.ward,
    id: userInfo._id
  } : null);
  
  // Prepare availability data for analysis
  const allAvailabilities = [];
  
  // Process availability data
  if (availabilitiesData?.availabilities) {
    Object.entries(availabilitiesData.availabilities).forEach(([dateString, availabilities]) => {
      if (availabilities.length > 0) {
        allAvailabilities.push(...availabilities);
      }
    });
  }
  
  if (allAvailabilities.length === 0) {
    showSnackbar('No availability data to analyze', 'warning');
    printWindow.close();
    return;
  }
  
  // Calculate analytics
  const analytics = calculateAvailabilityAnalytics(allAvailabilities);
  
  // Build the printable HTML with analysis
  const printHTML = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Staff Availability Analysis - ${monthInfo.monthName} ${monthInfo.year}</title>
      <style>
        @page {
          size: portrait;
          margin: 0.5in;
        }
        
        body {
          font-family: Arial, sans-serif;
          margin: 0;
          padding: 20px;
          background: white;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
          font-size: 10px;
          line-height: 1.4;
        }
        
        .print-header {
          text-align: center;
          margin-bottom: 30px;
          border-bottom: 3px solid #1976d2;
          padding-bottom: 15px;
        }
        
        .print-title {
          font-size: 28px;
          font-weight: bold;
          margin-bottom: 5px;
          color: #1976d2;
        }
        
        .print-subtitle {
          font-size: 18px;
          color: #666;
          margin-bottom: 10px;
        }
        
        .print-info {
          font-size: 12px;
          color: #666;
          margin-bottom: 5px;
        }
        
        .staff-info {
          padding: 20px;
          background-color: #f5f5f5;
          border-radius: 8px;
          margin: 20px auto;
          max-width: 800px;
          border: 2px solid #1976d2;
        }
        
        .staff-info div {
          margin-bottom: 8px;
          font-size: 11px;
        }
        
        .summary-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 15px;
          margin: 25px 0;
        }
        
        .stat-card {
          border: 1px solid #ddd;
          border-radius: 8px;
          padding: 15px;
          text-align: center;
          background-color: white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .stat-value {
          font-size: 24px;
          font-weight: bold;
          margin-bottom: 5px;
        }
        
        .stat-label {
          font-size: 11px;
          color: #666;
          text-transform: uppercase;
        }
        
        .section-title {
          font-size: 16px;
          font-weight: bold;
          color: #1976d2;
          margin: 25px 0 15px 0;
          padding-bottom: 8px;
          border-bottom: 2px solid #e0e0e0;
        }
        
        .breakdown-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 25px;
        }
        
        .breakdown-table th,
        .breakdown-table td {
          border: 1px solid #ddd;
          padding: 10px;
          text-align: left;
          font-size: 10px;
        }
        
        .breakdown-table th {
          background-color: #f5f5f5;
          font-weight: bold;
          text-transform: uppercase;
        }
        
        .breakdown-table tr:nth-child(even) {
          background-color: #f9f9f9;
        }
        
        .percentage-bar {
          background-color: #e0e0e0;
          height: 8px;
          border-radius: 4px;
          margin-top: 5px;
          overflow: hidden;
        }
        
        .percentage-fill {
          height: 100%;
          background-color: #1976d2;
          border-radius: 4px;
        }
        
        .status-badge {
          display: inline-block;
          padding: 3px 8px;
          border-radius: 12px;
          font-size: 9px;
          font-weight: bold;
          text-transform: uppercase;
        }
        
        .status-approved { background-color: #4caf50; color: white; }
        .status-pending { background-color: #2196f3; color: white; }
        .status-rejected { background-color: #f44336; color: white; }
        .status-unavailable { background-color: #d32f2f; color: white; }
        .status-sickleave { background-color: #ff9800; color: white; }
        
        .category-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 15px;
          margin-bottom: 25px;
        }
        
        .category-item {
          background-color: white;
          border: 1px solid #ddd;
          border-radius: 6px;
          padding: 12px;
        }
        
        .category-header {
          font-weight: bold;
          margin-bottom: 8px;
          color: #333;
        }
        
        .insight-box {
          background-color: #e3f2fd;
          border: 1px solid #bbdefb;
          border-radius: 8px;
          padding: 20px;
          margin: 25px 0;
        }
        
        .insight-title {
          font-size: 14px;
          font-weight: bold;
          color: #1976d2;
          margin-bottom: 10px;
        }
        
        .insight-list {
          list-style-type: none;
          padding-left: 0;
          margin: 0;
        }
        
        .insight-list li {
          margin-bottom: 8px;
          padding-left: 20px;
          position: relative;
        }
        
        .insight-list li:before {
          content: "•";
          color: #1976d2;
          font-size: 18px;
          position: absolute;
          left: 0;
          top: -2px;
        }
        
        .footer {
          margin-top: 40px;
          padding-top: 20px;
          border-top: 1px solid #ddd;
          font-size: 9px;
          color: #666;
          text-align: center;
        }
        
        .highlight {
          color: #1976d2;
          font-weight: bold;
        }
        
        .unavailable-pay {
          color: #4caf50;
          font-weight: bold;
        }
        
        .unavailable-nopay {
          color: #f44336;
          font-weight: bold;
        }
        
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          body {
            font-size: 9pt;
          }
          
          .page-break {
            page-break-before: always;
          }
        }
      </style>
    </head>
    <body>
      <div class="print-header">
        <div class="print-title">Staff Availability Analysis Report</div>
        <div class="print-subtitle">${monthInfo.monthName} ${monthInfo.year}</div>
        <div class="print-info">Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
        <div class="print-info">Generated By: ${userInfo?.name || 'Manager'} (${userInfo?.email || ''})</div>
      </div>
      
      ${viewingStaff ? `
        <div class="staff-info">
          <div><strong>Staff Name:</strong> ${viewingStaff.name}</div>
          ${viewingStaff.jobtitle ? `<div><strong>Position:</strong> ${viewingStaff.jobtitle}</div>` : ''}
          ${viewingStaff.ward ? `<div><strong>Ward/Department:</strong> ${viewingStaff.ward}</div>` : ''}
          <div><strong>Report Period:</strong> ${monthInfo.monthName} ${monthInfo.year}</div>
          ${viewingStaff.id ? `<div><strong>Staff ID:</strong> ${viewingStaff.id}</div>` : ''}
          <div><strong>Total Records Analyzed:</strong> ${analytics.totalRecords}</div>
        </div>
      ` : ''}
      
      <!-- Executive Summary -->
      <div class="section-title">Executive Summary</div>
      <div style="margin-bottom: 30px; font-size: 11px; line-height: 1.6;">
        <p>
          This analysis covers ${analytics.totalRecords} availability records for ${viewingStaff?.name || 'selected staff'} 
          during ${monthInfo.monthName} ${monthInfo.year}. 
          ${analytics.totalAvailableHours > 0 ? 
            `Staff has scheduled <strong>${analytics.totalAvailableHours} available hours</strong> ` : 
            'No available hours scheduled. '
          }
          ${analytics.totalUnavailableHours > 0 ? 
            `with <strong>${analytics.totalUnavailableHours} unavailable hours</strong> ` : 
            ''
          }
          (${analytics.unavailablePercentage.toFixed(1)}% of total).
        </p>
        <p>
          ${analytics.approvedCount > 0 ? 
            `<strong>${analytics.approvedCount} shifts (${analytics.approvedPercentage.toFixed(1)}%)</strong> are approved. ` : 
            ''
          }
          ${analytics.pendingCount > 0 ? 
            `<strong>${analytics.pendingCount} shifts (${analytics.pendingPercentage.toFixed(1)}%)</strong> are pending approval. ` : 
            ''
          }
        </p>
      </div>
      
      <!-- Summary Statistics -->
      <div class="section-title">Key Statistics</div>
      <div class="summary-stats">
        <div class="stat-card">
          <div class="stat-value">${analytics.totalAvailableHours}h</div>
          <div class="stat-label">Total Available Hours</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">${analytics.totalShifts}</div>
          <div class="stat-label">Total Shifts</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">${analytics.unavailablePercentage.toFixed(1)}%</div>
          <div class="stat-label">Unavailable</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">${analytics.approvedPercentage.toFixed(1)}%</div>
          <div class="stat-label">Approved</div>
        </div>
      </div>
      
      <!-- Availability Breakdown -->
      <div class="section-title">Availability Breakdown by Type</div>
      <table class="breakdown-table">
        <thead>
          <tr>
            <th>Type</th>
            <th>Count</th>
            <th>Percentage</th>
            <th>Total Hours</th>
            <th>Average Duration</th>
          </tr>
        </thead>
        <tbody>
          ${analytics.breakdownByType.map(type => `
            <tr>
              <td>
                <div style="display: flex; align-items: center; gap: 8px;">
                  ${type.type === 'Available' ? '<span class="status-badge status-approved">✓ Available</span>' : 
                    type.type === 'Unavailable' ? '<span class="status-badge status-unavailable">⛔ Unavailable</span>' : 
                    '<span class="status-badge status-sickleave">🤒 Sick Leave</span>'}
                </div>
              </td>
              <td><strong>${type.count}</strong></td>
              <td>
                ${type.percentage.toFixed(1)}%
                <div class="percentage-bar">
                  <div class="percentage-fill" style="width: ${type.percentage}%"></div>
                </div>
              </td>
              <td>${type.totalHours}h</td>
              <td>${type.avgDuration}h</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      
      <!-- Pay Status Analysis -->
      <div class="section-title">Pay Status Analysis</div>
      <div class="category-grid">
        <div class="category-item">
          <div class="category-header">Unavailable Hours Marked "For Pay"</div>
          <div style="margin-top: 10px;">
            <div style="margin-bottom: 8px;">
              <span class="unavailable-pay">For Pay:</span> ${analytics.unavailablePayHours.totalHours}h 
              (${analytics.unavailablePayHours.percentage.toFixed(1)}% of unavailable hours)
            </div>
            <div class="percentage-bar">
              <div class="percentage-fill" style="width: ${analytics.unavailablePayHours.percentage}%; background-color: #4caf50;"></div>
            </div>
            <div style="margin-top: 8px; font-size: 9px; color: #666;">
              Remaining unavailable hours: ${(parseFloat(analytics.totalUnavailableHours) - parseFloat(analytics.unavailablePayHours.totalHours)).toFixed(1)}h
            </div>
          </div>
        </div>
        
        <div class="category-item">
          <div class="category-header">Unavailable Hours by Status</div>
          <div style="margin-top: 10px; font-size: 9px;">
            <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
              <span>For Pay Hours:</span>
              <strong>${analytics.unavailablePayHours.totalHours}h</strong>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
              <span>Regular Unavailable:</span>
              <strong>${(parseFloat(analytics.totalUnavailableHours) - parseFloat(analytics.unavailablePayHours.totalHours)).toFixed(1)}h</strong>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
              <span>Total Unavailable:</span>
              <strong>${analytics.totalUnavailableHours}h</strong>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Category of Staff Analysis -->
      <div class="section-title">Category of Staff Analysis</div>
      <table class="breakdown-table">
        <thead>
          <tr>
            <th>Category</th>
            <th>Count</th>
            <th>Percentage</th>
            <th>Available Hours</th>
            <th>Unavailable Hours</th>
            <th>For Pay Hours</th>
          </tr>
        </thead>
        <tbody>
          ${analytics.categoryBreakdown.map(category => `
            <tr>
              <td><strong>${category.category || 'Not Specified'}</strong></td>
              <td>${category.count}</td>
              <td>${category.percentage.toFixed(1)}%</td>
              <td>${category.availableHours}h</td>
              <td>${category.unavailableHours}h</td>
              <td>${category.forPayHours}h</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      
      <!-- Status and Line Status Analysis -->
      <div class="section-title">Status Distribution</div>
      <div class="category-grid">
        <div class="category-item">
          <div class="category-header">Approval Status</div>
          <table style="width: 100%; margin-top: 10px; font-size: 9px;">
            ${Object.entries(analytics.statusDistribution).map(([status, data]) => `
              <tr>
                <td>
                  <span class="status-badge status-${status.toLowerCase()}">${status}</span>
                </td>
                <td>${data.count}</td>
                <td>${data.percentage.toFixed(1)}%</td>
                <td>
                  <div class="percentage-bar" style="height: 6px; margin-top: 4px;">
                    <div class="percentage-fill" style="width: ${data.percentage}%"></div>
                  </div>
                </td>
              </tr>
            `).join('')}
          </table>
        </div>
        
        <div class="category-item">
          <div class="category-header">Line Status</div>
          <table style="width: 100%; margin-top: 10px; font-size: 9px;">
            ${Object.entries(analytics.lineStatusDistribution).map(([lineStatus, data]) => `
              <tr>
                <td>${lineStatus || 'Not Set'}</td>
                <td>${data.count}</td>
                <td>${data.percentage.toFixed(1)}%</td>
                <td>
                  <div class="percentage-bar" style="height: 6px; margin-top: 4px;">
                    <div class="percentage-fill" style="width: ${data.percentage}%"></div>
                  </div>
                </td>
              </tr>
            `).join('')}
          </table>
        </div>
      </div>
      
      <!-- Day-wise Analysis -->
      <div class="section-title">Day-wise Analysis</div>
      <table class="breakdown-table">
        <thead>
          <tr>
            <th>Day</th>
            <th>Total Shifts</th>
            <th>Available Hours</th>
            <th>Unavailable Hours</th>
            <th>For Pay Hours</th>
            <th>Utilization %</th>
          </tr>
        </thead>
        <tbody>
          ${analytics.dayBreakdown.map(day => `
            <tr>
              <td><strong>${day.day}</strong></td>
              <td>${day.totalShifts}</td>
              <td>${day.availableHours}h</td>
              <td>${day.unavailableHours}h</td>
              <td>${day.forPayHours}h</td>
              <td>
                ${day.utilizationPercentage}%
                <div class="percentage-bar">
                  <div class="percentage-fill" style="width: ${day.utilizationPercentage}%"></div>
                </div>
              </td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      
      <!-- Unavailability Reasons -->
      <div class="section-title">Top Unavailability Reasons</div>
      <table class="breakdown-table">
        <thead>
          <tr>
            <th>Reason</th>
            <th>Count</th>
            <th>Percentage</th>
            <th>Distribution</th>
          </tr>
        </thead>
        <tbody>
          ${analytics.unavailableReasons.slice(0, 10).map(reason => `
            <tr>
              <td>${reason.reason}</td>
              <td>${reason.count}</td>
              <td>${reason.percentage.toFixed(1)}%</td>
              <td>
                <div class="percentage-bar">
                  <div class="percentage-fill" style="width: ${reason.percentage}%"></div>
                </div>
              </td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      
      <!-- Key Insights -->
      <div class="insight-box">
        <div class="insight-title">Insights</div>
        <ul class="insight-list">
          <li>Overall availability utilization: <span class="highlight">${analytics.utilizationPercentage.toFixed(1)}%</span></li>
          ${analytics.insights.slice(0, 5).map(insight => `<li>${insight}</li>`).join('')}
          ${analytics.unavailablePayHours.totalHours > 0 ? `
            <li><span class="unavailable-pay">${analytics.unavailablePayHours.totalHours}h</span> of unavailable time is marked "For Pay"</li>
          ` : ''}
          ${analytics.topReason ? `
            <li>Most common unavailability reason: <strong>${analytics.topReason}</strong></li>
          ` : ''}
          <li>Average shift duration: ${analytics.breakdownByType.length > 0 ? 
            analytics.breakdownByType.reduce((sum, type) => sum + parseFloat(type.avgDuration), 0) / analytics.breakdownByType.length : 0}h</li>
        </ul>
      </div>
      
      <div class="footer">
        Beaumont Hospital Staff Availability System - Analytics Report<br>
        Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}<br>
        For questions or detailed analysis, contact: beaumontNursingBank@beaumont.ie
      </div>
    </body>
    </html>
  `;
  
  // Write to the print window and handle printing
  try {
    printWindow.document.open();
    printWindow.document.write(printHTML);
    printWindow.document.close();
    
    // Wait for the content to load before printing
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 500);
    };
    
    // Fallback if onload doesn't fire
    setTimeout(() => {
      if (printWindow.document.readyState === 'complete') {
        printWindow.print();
      }
    }, 1000);
    
  } catch (error) {
    console.error('Error generating print analysis:', error);
    showSnackbar('Error generating analysis report', 'error');
    printWindow.close();
  }
};

const handlePrintAnalysis = async () => {
  setAnalysisLoading(true);
  
  try {
    let allAvailabilities = [];
    let staffDataForAnalysis = [];
    let viewingStaff = null;
    
    if (analysisType === 'selectedStaff') {
      // Single staff analysis
      viewingStaff = selectedStaff || (userInfo && !userInfo.isManager && !userInfo.isAdmin ? {
        name: userInfo.name,
        jobtitle: userInfo.jobtitle,
        ward: userInfo.ward,
        id: userInfo._id
      } : null);
      
      if (!viewingStaff) {
        showSnackbar('Please select a staff member first', 'warning');
        setAnalysisLoading(false);
        return;
      }
      
      // Process availability data
      if (availabilitiesData?.availabilities) {
        Object.entries(availabilitiesData.availabilities).forEach(([dateString, availabilities]) => {
          if (availabilities.length > 0) {
            allAvailabilities.push(...availabilities.map(avail => ({
              ...avail,
              staffName: viewingStaff.name,
              staffJobTitle: viewingStaff.jobtitle
            })));
          }
        });
      }
      
      staffDataForAnalysis = [{
        staff: viewingStaff,
        availabilities: allAvailabilities
      }];
      
    } else if (analysisType === 'jobTitle') {
      // Job title group analysis
      const staffToAnalyze = selectedJobTitleForAnalysis === 'all' 
        ? staffList 
        : staffList.filter(staff => staff.jobtitle === selectedJobTitleForAnalysis);
      
      if (staffToAnalyze.length === 0) {
        showSnackbar('No staff found with the selected criteria', 'warning');
        setAnalysisLoading(false);
        return;
      }
      
      showSnackbar(`Fetching data for ${staffToAnalyze.length} staff members...`, 'info');
      
      // Fetch availability for each staff member using the existing API
      for (const staff of staffToAnalyze) {
        try {
          // Use fetch to get the data
          const response = await fetch(
            `/api/availability/monthly?year=${analysisYear}&month=${analysisMonth + 1}&staffId=${staff._id}`
          );
          
          if (!response.ok) {
            console.error(`Failed to fetch for ${staff.name}`);
            continue;
          }
          
          const data = await response.json();
          
          if (data.availabilities) {
            const staffAvailabilities = Object.values(data.availabilities).flat();
            
            // Add staff info to each availability
            const availabilitiesWithStaffInfo = staffAvailabilities.map(avail => ({
              ...avail,
              staffName: staff.name,
              staffJobTitle: staff.jobtitle,
              staffWard: staff.ward
            }));
            
            allAvailabilities.push(...availabilitiesWithStaffInfo);
            
            staffDataForAnalysis.push({
              staff: {
                name: staff.name,
                jobtitle: staff.jobtitle,
                ward: staff.ward,
                id: staff._id,
                email: staff.email
              },
              availabilities: staffAvailabilities
            });
          }
        } catch (error) {
          console.error(`Error fetching data for ${staff.name}:`, error);
          // Continue with next staff even if one fails
        }
      }
      
      if (allAvailabilities.length === 0) {
        showSnackbar('No availability data found for the selected group', 'warning');
        setAnalysisLoading(false);
        return;
      }
    }
    
    // Calculate analytics
    const analytics = calculateAvailabilityAnalytics(allAvailabilities, staffDataForAnalysis);
    
    // Add group-specific data
    if (analysisType === 'jobTitle') {
      analytics.groupAnalysis = {
        type: 'jobTitle',
        value: selectedJobTitleForAnalysis,
        staffCount: staffDataForAnalysis.length,
        staffList: staffDataForAnalysis.map(s => ({
          name: s.staff.name,
          jobtitle: s.staff.jobtitle,
          ward: s.staff.ward,
          availableHours: s.availabilities.filter(a => !a.isUnavailable && !a.isSickLeave)
            .reduce((sum, a) => sum + (a.durationMinutes / 60), 0).toFixed(1),
          unavailableHours: s.availabilities.filter(a => a.isUnavailable || a.isSickLeave)
            .reduce((sum, a) => sum + (a.durationMinutes / 60), 0).toFixed(1),
          forPayHours: s.availabilities.filter(a => (a.isUnavailable || a.isSickLeave) && a.unavailablePayHours)
            .reduce((sum, a) => sum + (a.durationMinutes / 60), 0).toFixed(1)
        }))
      };
    }
    
    // Generate the print report
    generateAnalysisReport(analytics, analysisType, selectedJobTitleForAnalysis, analysisMonth, analysisYear, staffDataForAnalysis, viewingStaff);
    
  } catch (error) {
    console.error('Error in analysis:', error);
    showSnackbar('Error generating analysis report', 'error');
  } finally {
    setAnalysisLoading(false);
  }
};


// Helper function to calculate availability analytics
const calculateAvailabilityAnalytics = (availabilities) => {
  let totalHours = 0;
  let totalAvailableHours = 0;
  let totalUnavailableHours = 0;
  let totalForPayHours = 0;
  let approvedCount = 0;
  let pendingCount = 0;
  let rejectedCount = 0;
  let availableCount = 0;
  let unavailableCount = 0;
  let sickLeaveCount = 0;
  
  // Initialize breakdown objects
  const statusDistribution = {};
  const lineStatusDistribution = {};
  const categoryBreakdown = {};
  const unavailableReasons = {};
  const dayBreakdown = {
    Monday: { totalShifts: 0, availableHours: 0, unavailableHours: 0, forPayHours: 0 },
    Tuesday: { totalShifts: 0, availableHours: 0, unavailableHours: 0, forPayHours: 0 },
    Wednesday: { totalShifts: 0, availableHours: 0, unavailableHours: 0, forPayHours: 0 },
    Thursday: { totalShifts: 0, availableHours: 0, unavailableHours: 0, forPayHours: 0 },
    Friday: { totalShifts: 0, availableHours: 0, unavailableHours: 0, forPayHours: 0 },
    Saturday: { totalShifts: 0, availableHours: 0, unavailableHours: 0, forPayHours: 0 },
    Sunday: { totalShifts: 0, availableHours: 0, unavailableHours: 0, forPayHours: 0 }
  };
  
  // Process each availability
  availabilities.forEach(availability => {
    

    const hours = availability.durationMinutes / 60;
    totalHours += hours;
    
    // Get day of week
    const startDate = new Date(availability.startDateTime);
    const dayOfWeek = startDate.toLocaleDateString('en-US', { weekday: 'long' });
    
    // Update day breakdown
    dayBreakdown[dayOfWeek].totalShifts += 1;
    

    
    // Categorize by availability type
    if (availability.isUnavailable || availability.isSickLeave) {
      unavailableCount += 1;
      totalUnavailableHours += hours;
      dayBreakdown[dayOfWeek].unavailableHours += hours;
      
      // Track unavailable for pay
      if (availability.unavailablePayHours) {
        totalForPayHours += hours;
        dayBreakdown[dayOfWeek].forPayHours += hours;
      }
      
      // Track unavailable reasons
      const reason = availability.isSickLeave ? 
        `Sick Leave: ${availability.sicknessReason || 'Not specified'}` :
        availability.unavailabilityReason || 'Not specified';
      
      if (!unavailableReasons[reason]) {
        unavailableReasons[reason] = 0;
      }
      unavailableReasons[reason] += 1;
      
      if (availability.isSickLeave) {
        sickLeaveCount += 1;
      }
    } else {
      availableCount += 1;
      totalAvailableHours += hours;
      dayBreakdown[dayOfWeek].availableHours += hours;
    }
    
    // Track status distribution
    const status = availability.status || 'Open';
    if (!statusDistribution[status]) {
      statusDistribution[status] = { count: 0, totalHours: 0 };
    }
    statusDistribution[status].count += 1;
    statusDistribution[status].totalHours += hours;
    
    // Track line status distribution
    const lineStatus = availability.lineStatus || 'Not Set';
    if (!lineStatusDistribution[lineStatus]) {
      lineStatusDistribution[lineStatus] = { count: 0, totalHours: 0 };
    }
    lineStatusDistribution[lineStatus].count += 1;
    lineStatusDistribution[lineStatus].totalHours += hours;
    
    // Track category of staff
    const category = availability.categoryOfStaff || 'Not Specified';
    const staffId = availability.requestedBy?._id || availability.requestedBy;


    if (!categoryBreakdown[category]) {
      categoryBreakdown[category] = { 
        count: 0, 
        staffCount: new Set(),
        availableHours: 0, 
        unavailableHours: 0,
        forPayHours: 0 
      };
    }
    
    categoryBreakdown[category].count += 1;
    if (staffId) {
      categoryBreakdown[category].staffCount.add(staffId);
    }

    
    if (availability.isUnavailable || availability.isSickLeave) {
      categoryBreakdown[category].unavailableHours += hours;
      if (availability.unavailablePayHours) {
        categoryBreakdown[category].forPayHours += hours;
      }
    } else {
      categoryBreakdown[category].availableHours += hours;
    }
    
    // Count approvals
    if (availability.status === 'Approved') {
      approvedCount += 1;
    } else if (availability.status === 'Rejected') {
      rejectedCount += 1;
    } else {
      pendingCount += 1;
    }
  });
  
  // Calculate percentages and prepare breakdowns
  const totalRecords = availabilities.length;
  
  // Status distribution with percentages
  const statusDistributionWithPercentages = {};
  Object.keys(statusDistribution).forEach(status => {
    statusDistributionWithPercentages[status] = {
      count: statusDistribution[status].count,
      percentage: (statusDistribution[status].count / totalRecords) * 100
    };
  });
  
  // Line status distribution with percentages
  const lineStatusDistributionWithPercentages = {};
  Object.keys(lineStatusDistribution).forEach(lineStatus => {
    lineStatusDistributionWithPercentages[lineStatus] = {
      count: lineStatusDistribution[lineStatus].count,
      percentage: (lineStatusDistribution[lineStatus].count / totalRecords) * 100
    };
  });
  
  
  // Category breakdown with percentages
  const categoryBreakdownArray = Object.keys(categoryBreakdown).map(category => ({
    category,
    count: categoryBreakdown[category].count,
    staffCount: categoryBreakdown[category].staffCount.size,
    percentage: (categoryBreakdown[category].count / totalRecords) * 100,
    availableHours: categoryBreakdown[category].availableHours.toFixed(1),
    unavailableHours: categoryBreakdown[category].unavailableHours.toFixed(1),
    forPayHours: categoryBreakdown[category].forPayHours.toFixed(1)
  }));
  
  // Unavailable reasons with percentages
  const unavailableReasonsArray = Object.keys(unavailableReasons).map(reason => ({
    reason,
    count: unavailableReasons[reason],
    percentage: (unavailableReasons[reason] / unavailableCount) * 100
  })).sort((a, b) => b.count - a.count);
  
  // Day breakdown with utilization
  const dayBreakdownArray = Object.keys(dayBreakdown).map(day => {
    const dayData = dayBreakdown[day];
    const totalDayHours = dayData.availableHours + dayData.unavailableHours;
    const utilizationPercentage = totalDayHours > 0 ? 
      (dayData.availableHours / totalDayHours) * 100 : 0;
    
    return {
      day,
      totalShifts: dayData.totalShifts,
      availableHours: dayData.availableHours.toFixed(1),
      unavailableHours: dayData.unavailableHours.toFixed(1),
      forPayHours: dayData.forPayHours.toFixed(1),
      utilizationPercentage: utilizationPercentage.toFixed(1),
      categoryBreakdown: categoryBreakdownArray,
    };
  });
  
  // Availability type breakdown
  const breakdownByType = [
    {
      type: 'Available',
      count: availableCount,
      percentage: (availableCount / totalRecords) * 100,
      totalHours: totalAvailableHours.toFixed(1),
      avgDuration: availableCount > 0 ? (totalAvailableHours / availableCount).toFixed(1) : '0.0',
      statusBreakdown: statusDistributionWithPercentages
    },
    {
      type: 'Unavailable',
      count: unavailableCount - sickLeaveCount,
      percentage: ((unavailableCount - sickLeaveCount) / totalRecords) * 100,
      totalHours: (totalUnavailableHours - (totalForPayHours)).toFixed(1),
      avgDuration: (unavailableCount - sickLeaveCount) > 0 ? 
        ((totalUnavailableHours - totalForPayHours) / (unavailableCount - sickLeaveCount)).toFixed(1) : '0.0'
    },
    {
      type: 'Sick Leave',
      count: sickLeaveCount,
      percentage: (sickLeaveCount / totalRecords) * 100,
      totalHours: totalForPayHours.toFixed(1),
      avgDuration: sickLeaveCount > 0 ? (totalForPayHours / sickLeaveCount).toFixed(1) : '0.0'
    }
  ];
  
  // Generate insights
  const insights = [];
  
  if (totalAvailableHours > totalUnavailableHours) {
    insights.push(`Staff has more available hours (${totalAvailableHours.toFixed(1)}h) than unavailable hours (${totalUnavailableHours.toFixed(1)}h)`);
  } else if (totalUnavailableHours > totalAvailableHours) {
    insights.push(`Staff has more unavailable hours (${totalUnavailableHours.toFixed(1)}h) than available hours (${totalAvailableHours.toFixed(1)}h)`);
  }
  
  if (totalForPayHours > 0) {
    insights.push(`${totalForPayHours.toFixed(1)}h of unavailable time (${((totalForPayHours / totalUnavailableHours) * 100).toFixed(1)}%) is marked for pay calculation`);
  }
  
  if (pendingCount > 0) {
    insights.push(`${pendingCount} shifts (${((pendingCount / totalRecords) * 100).toFixed(1)}%) are pending approval`);
  }
  
  // Find highest utilization day
  const sortedDays = [...dayBreakdownArray].sort((a, b) => 
    parseFloat(b.utilizationPercentage) - parseFloat(a.utilizationPercentage)
  );
  
  if (sortedDays.length > 0 && parseFloat(sortedDays[0].utilizationPercentage) > 0) {
    insights.push(`Highest availability utilization: ${sortedDays[0].day} (${sortedDays[0].utilizationPercentage}%)`);
  }
  
  return {
    totalRecords,
    totalHours: totalHours.toFixed(1),
    totalAvailableHours: totalAvailableHours.toFixed(1),
    totalUnavailableHours: totalUnavailableHours.toFixed(1),
    totalShifts: totalRecords,
    availableCount,
    unavailableCount,
    sickLeaveCount,
    approvedCount,
    pendingCount,
    rejectedCount,
    approvedPercentage: (approvedCount / totalRecords) * 100,
    pendingPercentage: (pendingCount / totalRecords) * 100,
    unavailablePercentage: (unavailableCount / totalRecords) * 100,
    utilizationPercentage: totalHours > 0 ? (totalAvailableHours / totalHours) * 100 : 0,
    unavailablePayHours: {
      totalHours: totalForPayHours.toFixed(1),
      percentage: totalUnavailableHours > 0 ? (totalForPayHours / totalUnavailableHours) * 100 : 0
    },
    unavailableReasons: unavailableReasonsArray,
    topReason: unavailableReasonsArray.length > 0 ? unavailableReasonsArray[0].reason : null,
    breakdownByType,
    statusDistribution: statusDistributionWithPercentages,
    lineStatusDistribution: lineStatusDistributionWithPercentages,
    categoryBreakdown: categoryBreakdownArray,
    dayBreakdown: dayBreakdownArray,
    insights
  };
};

const generateAnalysisReport = (analytics, analysisType, jobTitle, month, year, staffData, viewingStaff) => {
  try {
    // Create a hidden iframe instead of a new window
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    iframe.style.visibility = 'hidden';
    document.body.appendChild(iframe);
    
    const monthName = monthNames[month];
    
    let reportTitle = '';
    let reportSubtitle = '';
    let groupSummarySection = '';
    let groupAnalysisData = {}; // To store group analysis data
    
    if (analysisType === 'selectedStaff') {
      // Single staff analysis - keep existing format
      reportTitle = 'Staff Availability Analysis Report';
      reportSubtitle = `${viewingStaff?.name || 'Selected Staff'} • ${monthName} ${year}`;
      
      groupSummarySection = `
        <div class="staff-info">
          <div><strong>Staff Name:</strong> ${viewingStaff?.name || 'Selected staff'}</div>
          ${viewingStaff?.jobtitle ? `<div><strong>Position:</strong> ${viewingStaff.jobtitle}</div>` : ''}
          ${viewingStaff?.ward ? `<div><strong>Ward/Department:</strong> ${viewingStaff.ward}</div>` : ''}
          <div><strong>Report Period:</strong> ${monthName} ${year}</div>
          <div><strong>Total Records Analyzed:</strong> ${analytics.totalRecords}</div>
        </div>
      `;
      
    } else {
      // Group analysis - new aggregated summary format
      reportTitle = 'Group Availability Analysis Report';
      reportSubtitle = `${jobTitle === 'all' ? 'All Job Titles' : `Job Title: ${jobTitle}`} • ${monthName} ${year}`;
      
      // Calculate comprehensive group statistics
      const totalAvailableHours = staffData.reduce((sum, s) => 
        sum + s.availabilities.filter(a => !a.isUnavailable && !a.isSickLeave)
          .reduce((hours, a) => hours + (a.durationMinutes / 60), 0), 0);
      
      const totalUnavailableHours = staffData.reduce((sum, s) => 
        sum + s.availabilities.filter(a => a.isUnavailable || a.isSickLeave)
          .reduce((hours, a) => hours + (a.durationMinutes / 60), 0), 0);
      
      const totalForPayHours = staffData.reduce((sum, s) => 
        sum + s.availabilities.filter(a => (a.isUnavailable || a.isSickLeave) && a.unavailablePayHours)
          .reduce((hours, a) => hours + (a.durationMinutes / 60), 0), 0);
      
      const totalHours = totalAvailableHours + totalUnavailableHours;
      const utilizationPercentage = totalHours > 0 ? (totalAvailableHours / totalHours) * 100 : 0;
      
      // Calculate per staff averages
      const avgAvailablePerStaff = staffData.length > 0 ? totalAvailableHours / staffData.length : 0;
      const avgUnavailablePerStaff = staffData.length > 0 ? totalUnavailableHours / staffData.length : 0;
      
      // Analyze staff categories
      const categoryAnalysis = {};
      staffData.forEach(staff => {
        const category = staff.staff.jobtitle || 'Not Specified';
        if (!categoryAnalysis[category]) {
          categoryAnalysis[category] = {
            count: 0,
            totalAvailableHours: 0,
            totalUnavailableHours: 0,
            staffNames: []
          };
        }
        categoryAnalysis[category].count++;
        categoryAnalysis[category].staffNames.push(staff.staff.name);
        
        const staffAvailableHours = staff.availabilities.filter(a => !a.isUnavailable && !a.isSickLeave)
          .reduce((sum, a) => sum + (a.durationMinutes / 60), 0);
        const staffUnavailableHours = staff.availabilities.filter(a => a.isUnavailable || a.isSickLeave)
          .reduce((sum, a) => sum + (a.durationMinutes / 60), 0);
        
        categoryAnalysis[category].totalAvailableHours += staffAvailableHours;
        categoryAnalysis[category].totalUnavailableHours += staffUnavailableHours;
      });
      
      // Prepare top performers analysis
      const staffPerformance = staffData.map(staff => {
        const availableHours = staff.availabilities.filter(a => !a.isUnavailable && !a.isSickLeave)
          .reduce((sum, a) => sum + (a.durationMinutes / 60), 0);
        const unavailableHours = staff.availabilities.filter(a => a.isUnavailable || a.isSickLeave)
          .reduce((sum, a) => sum + (a.durationMinutes / 60), 0);
        const totalStaffHours = availableHours + unavailableHours;
        const utilization = totalStaffHours > 0 ? (availableHours / totalStaffHours) * 100 : 0;
        const percentageOfTotalAvailable = totalAvailableHours > 0 ? (availableHours / totalAvailableHours) * 100 : 0;
        const percentageOfTotalUnavailable = totalUnavailableHours > 0 ? (unavailableHours / totalUnavailableHours) * 100 : 0;
        
        return {
          name: staff.staff.name,
          jobtitle: staff.staff.jobtitle || 'Not Specified',
          ward: staff.staff.ward || 'Not Specified',
          availableHours,
          unavailableHours,
          utilization,
          percentageOfTotalAvailable,
          percentageOfTotalUnavailable
        };
      });
      
      // Sort by available hours (descending)
      const topAvailableStaff = [...staffPerformance].sort((a, b) => b.availableHours - a.availableHours);
      // Sort by unavailable hours (descending)
      const topUnavailableStaff = [...staffPerformance].sort((a, b) => b.unavailableHours - a.unavailableHours);
      
      // Store group analysis data for use in insights
      groupAnalysisData = {
        totalAvailableHours,
        totalUnavailableHours,
        totalForPayHours,
        utilizationPercentage,
        avgAvailablePerStaff,
        avgUnavailablePerStaff,
        categoryAnalysis,
        staffDataLength: staffData.length,
        categoryCount: Object.keys(categoryAnalysis).length,
        topCategory: Object.entries(categoryAnalysis).sort((a, b) => b[1].totalAvailableHours - a[1].totalAvailableHours)[0]?.[0] || 'N/A'
      };
      
      groupSummarySection = `
        <div class="staff-info">
          <div><strong>Analysis Type:</strong> Group Analysis by Job Title</div>
          <div><strong>Job Title:</strong> ${jobTitle === 'all' ? 'All Job Titles' : jobTitle}</div>
          <div><strong>Staff Count:</strong> ${staffData.length} staff members</div>
          <div><strong>Report Period:</strong> ${monthName} ${year}</div>
          <div><strong>Total Available Hours:</strong> ${totalAvailableHours.toFixed(1)}h</div>
          <div><strong>Total Unavailable Hours:</strong> ${totalUnavailableHours.toFixed(1)}h</div>
          <div><strong>For Pay Hours:</strong> ${totalForPayHours.toFixed(1)}h</div>
          <div><strong>Group Utilization Rate:</strong> ${utilizationPercentage.toFixed(1)}%</div>
          <div><strong>Average Available per Staff:</strong> ${avgAvailablePerStaff.toFixed(1)}h</div>
          <div><strong>Average Unavailable per Staff:</strong> ${avgUnavailablePerStaff.toFixed(1)}h</div>
        </div>
        
        <!-- Staff Categories Breakdown -->
        <div class="section-title">Staff Categories Breakdown</div>
        <table class="breakdown-table">
          <thead>
            <tr>
              <th>Category (Job Title)</th>
              <th>Staff Count</th>
              <th>% of Total Staff</th>
              <th>Available Hours</th>
              <th>Unavailable Hours</th>
              <th>Avg Hours per Staff</th>
            </tr>
          </thead>
          <tbody>
            ${Object.entries(categoryAnalysis).map(([category, data]) => {
              const percentageOfStaff = (data.count / staffData.length) * 100;
              const avgAvailable = data.totalAvailableHours / data.count;
              const avgUnavailable = data.totalUnavailableHours / data.count;
              
              return `
                <tr>
                  <td><strong>${category}</strong></td>
                  <td>${data.count}</td>
                  <td>
                    ${percentageOfStaff.toFixed(1)}%
                    <div class="percentage-bar" style="margin-top: 5px;">
                      <div class="percentage-fill" style="width: ${percentageOfStaff}%"></div>
                    </div>
                  </td>
                  <td>${data.totalAvailableHours.toFixed(1)}h</td>
                  <td>${data.totalUnavailableHours.toFixed(1)}h</td>
                  <td>${avgAvailable.toFixed(1)}h available / ${avgUnavailable.toFixed(1)}h unavailable</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
        
        <!-- Top 10 Staff by Availability -->
        <div class="section-title">Top 10 Staff by Available Hours</div>
        <table class="breakdown-table">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Staff Name</th>
              <th>Job Title</th>
              <th>Available Hours</th>
              <th>% of Total Available</th>
              <th>Utilization %</th>
            </tr>
          </thead>
          <tbody>
            ${topAvailableStaff.slice(0, 10).map((staff, index) => `
              <tr>
                <td><strong>${index + 1}</strong></td>
                <td>${staff.name}</td>
                <td>${staff.jobtitle}</td>
                <td>${staff.availableHours.toFixed(1)}h</td>
                <td>
                  ${staff.percentageOfTotalAvailable.toFixed(1)}%
                  <div class="percentage-bar" style="margin-top: 5px;">
                    <div class="percentage-fill" style="width: ${staff.percentageOfTotalAvailable}%"></div>
                  </div>
                </td>
                <td>
                  ${staff.utilization.toFixed(1)}%
                  <div class="percentage-bar" style="margin-top: 5px;">
                    <div class="percentage-fill" style="width: ${staff.utilization}%"></div>
                  </div>
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <!-- Top 10 Staff by Unavailability -->
        <div class="section-title">Top 10 Staff by Unavailable Hours</div>
        <table class="breakdown-table">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Staff Name</th>
              <th>Job Title</th>
              <th>Unavailable Hours</th>
              <th>% of Total Unavailable</th>
              <th>Utilization %</th>
            </tr>
          </thead>
          <tbody>
            ${topUnavailableStaff.slice(0, 10).map((staff, index) => `
              <tr>
                <td><strong>${index + 1}</strong></td>
                <td>${staff.name}</td>
                <td>${staff.jobtitle}</td>
                <td>${staff.unavailableHours.toFixed(1)}h</td>
                <td>
                  ${staff.percentageOfTotalUnavailable.toFixed(1)}%
                  <div class="percentage-bar" style="margin-top: 5px;">
                    <div class="percentage-fill" style="width: ${staff.percentageOfTotalUnavailable}%"></div>
                  </div>
                </td>
                <td>
                  ${staff.utilization.toFixed(1)}%
                  <div class="percentage-bar" style="margin-top: 5px;">
                    <div class="percentage-fill" style="width: ${staff.utilization}%"></div>
                  </div>
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <!-- Monthly Shift Distribution -->
        <div class="section-title">Monthly Shift Distribution Analysis</div>
        <table class="breakdown-table">
          <thead>
            <tr>
              <th>Day of Week</th>
              <th>Total Shifts</th>
              <th>% of Total Shifts</th>
              <th>Available Hours</th>
              <th>Unavailable Hours</th>
              <th>Utilization %</th>
            </tr>
          </thead>
          <tbody>
            ${analytics.dayBreakdown.map(day => {
              const dayData = analytics.dayBreakdown.find(d => d.day === day.day);
              const totalShifts = parseFloat(dayData.totalShifts);
              const percentageOfTotalShifts = analytics.totalShifts > 0 ? (totalShifts / analytics.totalShifts) * 100 : 0;
              
              return `
                <tr>
                  <td><strong>${day.day}</strong></td>
                  <td>${dayData.totalShifts}</td>
                  <td>
                    ${percentageOfTotalShifts.toFixed(1)}%
                    <div class="percentage-bar" style="margin-top: 5px;">
                      <div class="percentage-fill" style="width: ${percentageOfTotalShifts}%"></div>
                    </div>
                  </td>
                  <td>${dayData.availableHours}h</td>
                  <td>${dayData.unavailableHours}h</td>
                  <td>
                    ${dayData.utilizationPercentage}%
                    <div class="percentage-bar" style="margin-top: 5px;">
                      <div class="percentage-fill" style="width: ${dayData.utilizationPercentage}%"></div>
                    </div>
                  </td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      `;
    }
    
    // Generate the complete HTML
    const printHTML = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${reportTitle}</title>
        <style>
          /* Keep existing CSS styles */
          @page {
            size: portrait;
            margin: 0.5in;
          }
          
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            background: white;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
            font-size: 10px;
            line-height: 1.4;
          }
          
          .print-header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 3px solid #1976d2;
            padding-bottom: 15px;
          }
          
          .print-title {
            font-size: 28px;
            font-weight: bold;
            margin-bottom: 5px;
            color: #1976d2;
          }
          
          .print-subtitle {
            font-size: 18px;
            color: #666;
            margin-bottom: 10px;
          }
          
          .print-info {
            font-size: 12px;
            color: #666;
            margin-bottom: 5px;
          }
          
          .staff-info {
            padding: 20px;
            background-color: #f5f5f5;
            border-radius: 8px;
            margin: 20px auto;
            max-width: 800px;
            border: 2px solid #1976d2;
          }
          
          .staff-info div {
            margin-bottom: 8px;
            font-size: 11px;
          }
          
          .summary-stats {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 15px;
            margin: 25px 0;
          }
          
          .stat-card {
            border: 1px solid #ddd;
            border-radius: 8px;
            padding: 15px;
            text-align: center;
            background-color: white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
          
          .stat-value {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 5px;
          }
          
          .stat-label {
            font-size: 11px;
            color: #666;
            text-transform: uppercase;
          }
          
          .section-title {
            font-size: 16px;
            font-weight: bold;
            color: #1976d2;
            margin: 25px 0 15px 0;
            padding-bottom: 8px;
            border-bottom: 2px solid #e0e0e0;
          }
          
          .breakdown-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 25px;
          }
          
          .breakdown-table th,
          .breakdown-table td {
            border: 1px solid #ddd;
            padding: 10px;
            text-align: left;
            font-size: 10px;
          }
          
          .breakdown-table th {
            background-color: #f5f5f5;
            font-weight: bold;
            text-transform: uppercase;
          }
          
          .breakdown-table tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          
          .percentage-bar {
            background-color: #e0e0e0;
            height: 8px;
            border-radius: 4px;
            margin-top: 5px;
            overflow: hidden;
          }
          
          .percentage-fill {
            height: 100%;
            background-color: #1976d2;
            border-radius: 4px;
          }
          
          .status-badge {
            display: inline-block;
            padding: 3px 8px;
            border-radius: 12px;
            font-size: 9px;
            font-weight: bold;
            text-transform: uppercase;
          }
          
          .status-approved { background-color: #4caf50; color: white; }
          .status-pending { background-color: #2196f3; color: white; }
          .status-rejected { background-color: #f44336; color: white; }
          .status-unavailable { background-color: #d32f2f; color: white; }
          .status-sickleave { background-color: #ff9800; color: white; }
          
          .category-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
            margin-bottom: 25px;
          }
          
          .category-item {
            background-color: white;
            border: 1px solid #ddd;
            border-radius: 6px;
            padding: 12px;
          }
          
          .category-header {
            font-weight: bold;
            margin-bottom: 8px;
            color: #333;
          }
          
          .insight-box {
            background-color: #e3f2fd;
            border: 1px solid #bbdefb;
            border-radius: 8px;
            padding: 20px;
            margin: 25px 0;
          }
          
          .insight-title {
            font-size: 14px;
            font-weight: bold;
            color: #1976d2;
            margin-bottom: 10px;
          }
          
          .insight-list {
            list-style-type: none;
            padding-left: 0;
            margin: 0;
          }
          
          .insight-list li {
            margin-bottom: 8px;
            padding-left: 20px;
            position: relative;
          }
          
          .insight-list li:before {
            content: "•";
            color: #1976d2;
            font-size: 18px;
            position: absolute;
            left: 0;
            top: -2px;
          }
          
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            font-size: 9px;
            color: #666;
            text-align: center;
          }
          
          .highlight {
            color: #1976d2;
            font-weight: bold;
          }
          
          .unavailable-pay {
            color: #4caf50;
            font-weight: bold;
          }
          
          .unavailable-nopay {
            color: #f44336;
            font-weight: bold;
          }
          
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            
            body {
              font-size: 9pt;
            }
            
            .page-break {
              page-break-before: always;
            }
          }
        </style>
      </head>
      <body>
        <div class="print-header">
          <div class="print-title">${reportTitle}</div>
          <div class="print-subtitle">${reportSubtitle}</div>
          <div class="print-info">Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
          <div class="print-info">Generated By: ${userInfo?.name || 'Manager'} (${userInfo?.email || ''})</div>
        </div>
        
        ${groupSummarySection}
        
        <!-- Availability Breakdown (for both single and group) -->
        <div class="section-title">Availability Type Breakdown</div>
        <table class="breakdown-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Count</th>
              <th>Percentage</th>
              <th>Total Hours</th>
              <th>Average Duration</th>
            </tr>
          </thead>
          <tbody>
            ${analytics.breakdownByType.map(type => `
              <tr>
                <td>
                  <div style="display: flex; align-items: center; gap: 8px;">
                    ${type.type === 'Available' ? '<span class="status-badge status-approved">✓ Available</span>' : 
                      type.type === 'Unavailable' ? '<span class="status-badge status-unavailable">⛔ Unavailable</span>' : 
                      '<span class="status-badge status-sickleave">🤒 Sick Leave</span>'}
                  </div>
                </td>
                <td><strong>${type.count}</strong></td>
                <td>
                  ${type.percentage.toFixed(1)}%
                  <div class="percentage-bar">
                    <div class="percentage-fill" style="width: ${type.percentage}%"></div>
                  </div>
                </td>
                <td>${type.totalHours}h</td>
                <td>${type.avgDuration}h</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <!-- Pay Status Analysis -->
        <div class="section-title">Pay Status Analysis</div>
        <div class="category-grid">
          <div class="category-item">
            <div class="category-header">Unavailable Hours Marked "For Pay"</div>
            <div style="margin-top: 10px;">
              <div style="margin-bottom: 8px;">
                <span class="unavailable-pay">For Pay:</span> ${analytics.unavailablePayHours.totalHours}h 
                (${analytics.unavailablePayHours.percentage.toFixed(1)}% of unavailable hours)
              </div>
              <div class="percentage-bar">
                <div class="percentage-fill" style="width: ${analytics.unavailablePayHours.percentage}%; background-color: #4caf50;"></div>
              </div>
            </div>
          </div>
          
          <div class="category-item">
            <div class="category-header">Unavailability Status</div>
            <div style="margin-top: 10px; font-size: 9px;">
              <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
                <span>Total Unavailable Hours:</span>
                <strong>${analytics.totalUnavailableHours}h</strong>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
                <span>For Pay Hours:</span>
                <strong>${analytics.unavailablePayHours.totalHours}h</strong>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
                <span>Regular Unavailable:</span>
                <strong>${(parseFloat(analytics.totalUnavailableHours) - parseFloat(analytics.unavailablePayHours.totalHours)).toFixed(1)}h</strong>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Unavailability Reasons -->
        ${analytics.unavailableReasons.length > 0 ? `
          <div class="section-title">Top Unavailability Reasons</div>
          <table class="breakdown-table">
            <thead>
              <tr>
                <th>Reason</th>
                <th>Count</th>
                <th>Percentage</th>
                <th>Distribution</th>
              </tr>
            </thead>
            <tbody>
              ${analytics.unavailableReasons.slice(0, 10).map(reason => `
                <tr>
                  <td>${reason.reason}</td>
                  <td>${reason.count}</td>
                  <td>${reason.percentage.toFixed(1)}%</td>
                  <td>
                    <div class="percentage-bar">
                      <div class="percentage-fill" style="width: ${reason.percentage}%"></div>
                    </div>
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        ` : ''}
        
        <!-- Key Insights -->
        <div class="insight-box">
          <div class="insight-title">${analysisType === 'jobTitle' ? 'Group Performance Insights' : 'Individual Performance Insights'}</div>
          <ul class="insight-list">
            ${analysisType === 'jobTitle' ? `
              <li>Group consists of <strong>${staffData.length} staff members</strong> across <strong>${Object.keys(groupAnalysisData.categoryAnalysis || {}).length} different job titles</strong></li>
              <li>Total group utilization rate: <strong>${groupAnalysisData.utilizationPercentage ? groupAnalysisData.utilizationPercentage.toFixed(1) : 0}%</strong></li>
              <li>Average available hours per staff: <strong>${groupAnalysisData.avgAvailablePerStaff ? groupAnalysisData.avgAvailablePerStaff.toFixed(1) : 0}h</strong></li>
              <li>Average unavailable hours per staff: <strong>${groupAnalysisData.avgUnavailablePerStaff ? groupAnalysisData.avgUnavailablePerStaff.toFixed(1) : 0}h</strong></li>
              <li>Top performing category: <strong>${groupAnalysisData.topCategory || 'N/A'}</strong></li>
              ${groupAnalysisData.totalForPayHours > 0 ? `
                <li><span class="unavailable-pay">${groupAnalysisData.totalForPayHours.toFixed(1)}h</span> of group unavailable time is marked "For Pay"</li>
              ` : ''}
            ` : `
              <li>Overall availability utilization: <span class="highlight">${analytics.utilizationPercentage.toFixed(1)}%</span></li>
              ${analytics.insights.slice(0, 5).map(insight => `<li>${insight}</li>`).join('')}
              ${analytics.unavailablePayHours.totalHours > 0 ? `
                <li><span class="unavailable-pay">${analytics.unavailablePayHours.totalHours}h</span> of unavailable time is marked "For Pay"</li>
              ` : ''}
            `}
            ${analytics.topReason ? `
              <li>Most common unavailability reason: <strong>${analytics.topReason}</strong></li>
            ` : ''}
          </ul>
        </div>
        
        <div class="footer">
          Beaumont Hospital Staff Availability System - Analytics Report<br>
          Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}<br>
          For questions or detailed analysis, contact: beaumontNursingBank@beaumont.ie
        </div>
        
        <script>
          // Auto-print and close
          setTimeout(() => {
            window.print();
            // Close the iframe after printing (in iframe context)
            setTimeout(() => {
              window.frameElement && window.frameElement.remove();
            }, 1000);
          }, 500);
        </script>
      </body>
      </html>
    `;
    
    // Write to iframe
    iframe.contentDocument.open();
    iframe.contentDocument.write(printHTML);
    iframe.contentDocument.close();
    
    // Wait for iframe to load
    iframe.onload = () => {
      // The script in the iframe will handle printing
    };
    
  } catch (error) {
    console.error('Error generating report:', error);
    showSnackbar('Error generating report. Please try again.', 'error');
  }
};





  const isLoading = loadingAvailabilities || creating || updating || deleting || loadingStaff;
  
  if (isLoading && !availabilitiesData && !staffData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      <PrintStyles />
      
      {/* Navigation Breadcrumbs */}
      <Breadcrumbs 
        aria-label="breadcrumb" 
        separator={<NavigateNext fontSize="small" />}
        sx={{ mb: 2 }}
      >
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate('/dashboard');
          }}
        >
          Dashboard
        </Link>
        <Typography color="text.primary">
          {(userInfo?.isManager || userInfo?.isAdmin) ? 'Staff Availability Management' : 'My Availability'}
        </Typography>
      </Breadcrumbs>
      
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
            {(userInfo?.isManager || userInfo?.isAdmin) ? 'Staff Availability Management' : 'My Availability'}
          </Typography>
          {selectedStaff && (
            <Typography variant="subtitle1" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person fontSize="small" />
              Viewing: {selectedStaff.name} • {selectedStaff.jobtitle} • {selectedStaff.ward}
            </Typography>
          )}
          {!selectedStaff && (userInfo?.isManager || userInfo?.isAdmin) && (
            <Typography variant="subtitle1" color="text.secondary">
              Select a staff member to view/manage their availability
            </Typography>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* Print Button */}
          <Button
            variant="contained"
            startIcon={<Print />}
            onClick={handlePrint}
            disabled={isLoading || ((userInfo?.isManager || userInfo?.isAdmin) && !selectedStaff)}
            sx={{
              bgcolor: '#673ab7',
              '&:hover': {
                bgcolor: '#5e35b1'
              }
            }}
            className="no-print"
          >
            Print Schedule
          </Button>

           {/* NEW: Print Analysis Button */}
            {/* NEW: Print Analysis Button */}
            <Button
              variant="contained"
              startIcon={<Print />}
              onClick={() => setAnalysisDialogOpen(true)}
              disabled={isLoading || ((userInfo?.isManager || userInfo?.isAdmin) && !selectedStaff && analysisType === 'selectedStaff')}
              sx={{
                bgcolor: '#1976d2',
                '&:hover': {
                  bgcolor: '#1565c0'
                }
              }}
              className="no-print"
            >
              Print Analysis
            </Button>
        
          {/* Calendar Analytics Button */}
          <Button
            variant="outlined"
            startIcon={<CalendarToday />}
            onClick={() => navigate('/admin/MonthlyCalendarView')}
            className="no-print"
          >
            Calendar Analytics
          </Button>
          
          {/* Weekly Schedule Button */}
          <Button
            variant="outlined"
            startIcon={<Schedule />}
            onClick={() => navigate('/admin/staffWeeklySchedule')}
            className="no-print"
          >
            Weekly Schedule
          </Button>
        </Box>
      </Box>
      
      {/* Top Row: Staff Selection and Month Selection - Side by Side */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Staff Selection for Managers/Admins - Left Column */}
        {(userInfo?.isManager || userInfo?.isAdmin) && (
          <Grid item xs={6}>
            <Paper elevation={2} sx={{ p: 2, height: '100%', bgcolor: '#f5f5f5' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Group />
                  <Typography variant="subtitle1" fontWeight="bold">
                    Select Staff Member
                  </Typography>
                </Box>
                
                <Button 
                  size="small" 
                  onClick={() => {
                    setSelectedJobTitleFilter('all');
                    setSelectedStaff(null);
                  }}
                  disabled={selectedJobTitleFilter === 'all' && !selectedStaff}
                  startIcon={<FilterAltOff />}
                >
                  Clear
                </Button>
              </Box>
              
              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel>Filter by Job Title</InputLabel>
                <Select
                  value={selectedJobTitleFilter}
                  label="Filter by Job Title"
                  onChange={(e) => setSelectedJobTitleFilter(e.target.value)}
                  startAdornment={<FilterAlt fontSize="small" sx={{ mr: 1 }} />}
                  disabled={loadingStaff}
                >
                  <MenuItem value="all">
                    <em>All Job Titles</em>
                  </MenuItem>
                  {availableJobTitles.slice(1).map((title) => (
                    <MenuItem key={title} value={title}>
                      {title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Autocomplete
                options={filteredStaffList}
                getOptionLabel={(option) => {
                  const ward = option.ward || 
                              option.wardDepartmentArea || 
                              option.hospitalLocation?.wardDepartmentArea ||
                              'No Ward';
                  return `${option.name} - ${option.jobtitle || 'No Title'} (${ward})`;
                }}
                value={selectedStaff}
                onChange={(event, newValue) => {
                  setSelectedStaff(newValue);
                  if (newValue) {
                    setTimeout(() => refetchAvailabilities(), 100);
                  }
                }}
                isOptionEqualToValue={(option, value) => option._id === value?._id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Staff Member"
                    placeholder="Start typing to search staff..."
                    size="small"
                    error={false}
                    helperText={loadingStaff ? "Loading staff..." : ""}
                    disabled={loadingStaff}
                  />
                )}
                renderOption={(props, option) => {
                  const ward = option.ward || 
                              option.wardDepartmentArea || 
                              option.hospitalLocation?.wardDepartmentArea ||
                              'No Ward';
                  return (
                    <li {...props} key={option._id}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <Typography variant="body1" fontWeight="medium">
                          {option.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {option.jobtitle || 'No Title'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Ward: {ward} • Email: {option.email}
                        </Typography>
                      </Box>
                    </li>
                  );
                }}
                noOptionsText={loadingStaff ? "Loading..." : "No staff members found"}
                loading={loadingStaff}
                disabled={loadingStaff}
                clearOnBlur={false}
                autoHighlight
                fullWidth
              />
              
              {/* Selected Staff Info */}
              {selectedStaff && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'white', borderRadius: 1 }}>
                  <Typography variant="body2">
                    <strong>Selected:</strong> {selectedStaff.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {selectedStaff.jobtitle} • {selectedStaff.ward || 'No Ward'}
                  </Typography>
                </Box>
              )}
              
              {/* Staff Count */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Showing {filteredStaffList.length} staff member{filteredStaffList.length !== 1 ? 's' : ''}
                </Typography>
                {selectedJobTitleFilter !== 'all' && (
                  <Typography variant="caption" color="textSecondary">
                    Filtered by: {selectedJobTitleFilter}
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        )}
        
        {/* Month Selection - Right Column (or full width for regular users) */}
        <Grid item xs={(userInfo?.isManager || userInfo?.isAdmin) ? 6 : 12}>
          <Paper elevation={2} sx={{ p: 2, height: '100%', bgcolor: '#f5f5f5' }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Month Selection
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton onClick={() => navigateMonth(-1)} size="small">
                  <ArrowBackIos fontSize="small" />
                </IconButton>
                <Typography variant="h6">
                  {monthInfo.monthName} {monthInfo.year}
                </Typography>
                <IconButton onClick={() => navigateMonth(1)} size="small">
                  <ArrowForwardIos fontSize="small" />
                </IconButton>
              </Box>
              
              <Button 
                startIcon={<Today />} 
                onClick={goToCurrentMonth}
                variant="outlined"
                size="small"
              >
                Current Month
              </Button>
            </Box>
            
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Year</InputLabel>
                  <Select
                    value={selectedYear}
                    label="Year"
                    onChange={(e) => setSelectedYear(e.target.value)}
                  >
                    {generateYears().map(year => (
                      <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Month</InputLabel>
                  <Select
                    value={selectedMonth}
                    label="Month"
                    onChange={(e) => setSelectedMonth(e.target.value)}
                  >
                    {monthNames.map((month, index) => (
                      <MenuItem key={month} value={index}>{month}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            {/* Summary */}
            <Box sx={{ mt: 2, p: 1, bgcolor: 'white', borderRadius: 1 }}>
              <Typography variant="body2" color="textSecondary">
                <strong>Viewing:</strong> {getViewingStaffName()}'s Availability
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Total Hours:</strong> {formatHoursMinutes(
                  availabilitiesData?.availabilities 
                    ? Object.values(availabilitiesData.availabilities).flat()
                        .reduce((sum, a) => sum + a.durationMinutes, 0)
                    : 0
                )}
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                {monthInfo.monthName} {monthInfo.year} • {monthInfo.daysInMonth} days
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Calendar Grid */}
      <Paper elevation={3} sx={{ p: 2 }} id="availability-calendar-view">
        <Typography variant="h6" gutterBottom>
          Availability Calendar - {monthInfo.monthName} {monthInfo.year}
        </Typography>
        
         {/* Weekday Headers - Always show Monday to Sunday */}
  <Grid container spacing={1} sx={{ mb: 1 }}>
    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
      <Grid item xs key={day}>
        <Paper 
          elevation={0}
          sx={{ 
            p: 1, 
            textAlign: 'center',
            bgcolor: day === 'Saturday' || day === 'Sunday' ? 'rgba(255, 152, 0, 0.1)' : 'rgba(0, 0, 0, 0.04)',
            borderBottom: '2px solid',
            borderColor: day === 'Saturday' || day === 'Sunday' ? 'warning.light' : 'primary.light'
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            {day.substring(0, 3)}
          </Typography>
        </Paper>
      </Grid>
    ))}
  </Grid>

        {(userInfo?.isManager || userInfo?.isAdmin) && !selectedStaff ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Please select a staff member from the dropdown above to view and manage their availability.
            </Typography>
          </Alert>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Click on any day cell to add available hours for that day. 
                You can add multiple time slots per day (no overlapping times). 
                Drag availability blocks between days to move them. 
                Approved availabilities are shown in green.
                <strong> Unavailable periods are shown in red.</strong>
              </Typography>
            </Alert>
            
             {/* SCROLLABLE CONTAINER */}
       {/* SCROLLABLE CONTAINER */}
      <Box sx={scrollableTableStyle}>
        {/* Instead of using a single Grid container, group by weeks */}
        {(() => {
          const weeks = [];
          for (let i = 0; i < calendarDays.length; i += 7) {
            weeks.push(calendarDays.slice(i, i + 7));
          }
          
          return weeks.map((week, weekIndex) => (
            <Grid container spacing={1} key={weekIndex} sx={{ mb: 1 }}>
              {week.map((day) => (
                <Grid item xs key={day.date || `empty-${day.dayNumber}`}>
                  <Paper 
                    elevation={1}
                    sx={{ 
                      height: '140px',
                      border: 1,
                      borderColor: day.isInMonth ? 
                        (day.isWeekend ? 'warning.light' : 
                         day.isToday ? 'info.light' : 'divider') : 
                        'grey.300',
                      bgcolor: day.isInMonth ? 
                        (day.isWeekend ? 'rgba(255, 152, 0, 0.05)' : 
                         day.isToday ? 'rgba(33, 150, 243, 0.05)' : 'white') : 
                        'rgba(33, 150, 243, 0.05)',
                      overflow: 'hidden',
                      position: 'relative',
                      cursor: day.isInMonth ? 'pointer' : 'default',
                      opacity: day.isInMonth ? 1 : 0.7,
                      '&:hover': day.isInMonth ? {
                        boxShadow: 3,
                        transform: 'translateY(-2px)',
                        transition: 'all 0.2s'
                      } : {}
                    }}
                    onClick={() => {
                      if (day.isInMonth && day.date) {
                        if ((userInfo?.isManager || userInfo?.isAdmin) && !selectedStaff) {
                          showSnackbar('Please select a staff member first', 'warning');
                        } else {
                          handleDayClick(day);
                        }
                      }
                    }}
                    onDragOver={(e) => handleDragOver(day, e)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(day, e)}
                  >
                    {formatDayDisplay(day)}
                    
                    <Box sx={{ 
                      position: 'absolute',
                      top: 2,
                      left: 2,
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      bgcolor: !day.isInMonth ? 'grey.400' :
                              day.isWeekend ? 'warning.main' : 
                              day.isToday ? 'info.main' : 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography variant="caption" fontWeight="bold">
                        {day.dayNumber}
                      </Typography>
                    </Box>
                    
                    {/* Add indicator for days not in current month */}
                    {!day.isInMonth && (
                      <Box sx={{ 
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        fontSize: '0.7rem',
                        color: 'text.disabled',
                        fontStyle: 'italic',
                        textAlign: 'center'
                      }}>
                        {day.date ? (
                          <>
                            <Typography variant="caption" display="block">
                              {new Date(day.date).toLocaleDateString('en-US', { month: 'short' })}
                            </Typography>
                            <Typography variant="caption" display="block">
                              {day.dayName}
                            </Typography>
                          </>
                        ) : 'Not in month'}
                      </Box>
                    )}
                    
                    {/* Add button indicator for days in month without availabilities */}
                    {day.isInMonth && day.date && getDayAvailabilities(day.date).length === 0 && (
                      <Box sx={{ 
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: 'grey.300',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Add fontSize="small" />
                      </Box>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          ));
        })()}
      </Box>
            
            {/* Legend */}
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Color Legend:
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 16, height: 16, bgcolor: 'info.light', borderRadius: 1 }} />
                      <Typography variant="body2">Pending Availability</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 16, height: 16, bgcolor: 'success.light', borderRadius: 1 }} />
                      <Typography variant="body2">Approved Availability</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 16, height: 16, bgcolor: 'error.light', borderRadius: 1 }} />
                      <Typography variant="body2">Rejected Availability</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 16, height: 16, bgcolor: 'warning.light', borderRadius: 1 }} />
                      <Typography variant="body2">Weekend Days</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 16, height: 16, bgcolor: 'info.light', borderRadius: 1, opacity: 0.3 }} />
                      <Typography variant="body2">Today</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ 
                        width: 16, 
                        height: 16, 
                        borderRadius: 1,
                        border: '2px dashed #1976d2',
                        bgcolor: 'rgba(33, 150, 243, 0.1)'
                      }} />
                      <Typography variant="body2">Drag & Drop Target</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'white', borderRadius: 1 }}>
                    <Box sx={{ 
                      width: 24, 
                      height: 24, 
                      borderRadius: '50%',
                      bgcolor: 'grey.300',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Add fontSize="small" />
                    </Box>
                    <Typography variant="body2">
                      Click on any day cell to add/update availability
                      {(userInfo?.isManager || userInfo?.isAdmin) && selectedStaff && 
                        ` for ${selectedStaff.name}`}
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'white', borderRadius: 1 }}>
                    <DragIndicator color="action" />
                    <Typography variant="body2">
                      Drag availability blocks between days to move them
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </Paper>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteConfirmClose} maxWidth="sm" fullWidth>
        <DialogTitle>Remove Availability</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Are you sure you want to remove this availability?
          </Typography>
          {editingAvailability && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Time:</strong> {formatTime(editingAvailability.startDateTime)} - {formatTime(editingAvailability.endDateTime)}
              </Typography>
              <Typography variant="body2">
                <strong>Date:</strong> {formatDate(editingAvailability.startDateTime)}
              </Typography>
              {(editingAvailability.isUnavailable || editingAvailability.isSickLeave) && (
                <Typography variant="body2">
                  <strong>Type:</strong> Unavailable ({editingAvailability.unavailabilityReason || editingAvailability.sicknessReason || 'No reason specified'})
                </Typography>
              )}
            </Box>
          )}
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2, fontStyle: 'italic' }}>
            Note: This action can be reversed by an administrator.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteConfirmClose}>Cancel</Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained" 
            color="error"
            startIcon={<Delete />}
          >
            Remove Availability
          </Button>
        </DialogActions>
      </Dialog>


{/* Analysis Options Dialog */}
<Dialog open={analysisDialogOpen} onClose={() => setAnalysisDialogOpen(false)} maxWidth="sm" fullWidth>
  <DialogTitle>Print Analysis Options</DialogTitle>
  <DialogContent>
    <Typography variant="body1" gutterBottom>
      Choose what to analyze:
    </Typography>
    
    <Box sx={{ mb: 3 }}>
      <FormControl component="fieldset">
        <RadioGroup
          value={analysisType}
          onChange={(e) => setAnalysisType(e.target.value)}
        >
          <FormControlLabel 
            value="selectedStaff" 
            control={<Radio />} 
            label={
              <Box>
                <Typography variant="body2">
                  Selected Staff Member Only
                </Typography>
                {selectedStaff && (
                  <Typography variant="caption" color="textSecondary" sx={{ ml: 4 }}>
                    Currently: {selectedStaff.name} ({selectedStaff.jobtitle})
                  </Typography>
                )}
              </Box>
            }
          />
          <FormControlLabel 
            value="jobTitle" 
            control={<Radio />} 
            label={
              <Box>
                <Typography variant="body2">
                  All Staff with Specific Job Title
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ ml: 4 }}>
                  Group analysis by job title
                </Typography>
              </Box>
            }
          />
        </RadioGroup>
      </FormControl>
    </Box>
    
    {analysisType === 'jobTitle' && (
      <FormControl fullWidth size="small" sx={{ mb: 3 }}>
        <InputLabel>Select Job Title</InputLabel>
        <Select
          value={selectedJobTitleForAnalysis}
          label="Select Job Title"
          onChange={(e) => setSelectedJobTitleForAnalysis(e.target.value)}
        >
          <MenuItem value="all">
            <em>All Job Titles</em>
          </MenuItem>
          {availableJobTitles.slice(1).map((title) => (
            <MenuItem key={title} value={title}>
              {title}
            </MenuItem>
          ))}
        </Select>
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
          {selectedJobTitleForAnalysis === 'all' 
            ? 'Will include all available job titles' 
            : `Will include all staff with job title: ${selectedJobTitleForAnalysis}`}
        </Typography>
      </FormControl>
    )}
    
    <Divider sx={{ my: 2 }} />
    
    <Typography variant="subtitle2" gutterBottom>
      Analysis Period
    </Typography>
    
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <FormControl fullWidth size="small">
          <InputLabel>Year</InputLabel>
          <Select
            value={analysisYear}
            label="Year"
            onChange={(e) => setAnalysisYear(e.target.value)}
          >
            {generateYears().map(year => (
              <MenuItem key={year} value={year}>{year}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      
      <Grid item xs={6}>
        <FormControl fullWidth size="small">
          <InputLabel>Month</InputLabel>
          <Select
            value={analysisMonth}
            label="Month"
            onChange={(e) => setAnalysisMonth(e.target.value)}
          >
            {monthNames.map((month, index) => (
              <MenuItem key={month} value={index}>{month}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
    
    <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
      <Typography variant="caption" sx={{ display: 'block' }}>
        <strong>Report will include:</strong>
      </Typography>
      <Typography variant="caption" sx={{ display: 'block' }}>
        • {analysisType === 'selectedStaff' 
            ? `Single staff: ${selectedStaff?.name || 'Selected staff'}` 
            : selectedJobTitleForAnalysis === 'all'
              ? 'All job titles and staff'
              : `All staff with job title: ${selectedJobTitleForAnalysis}`}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block' }}>
        • Period: {monthNames[analysisMonth]} {analysisYear}
      </Typography>
      {analysisType === 'jobTitle' && (
        <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1 }}>
          Note: This may take longer as it analyzes multiple staff members
        </Typography>
      )}
    </Box>
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setAnalysisDialogOpen(false)}>
      Cancel
    </Button>
    <Button 
      onClick={() => {
        if (analysisType === 'jobTitle' && !selectedJobTitleForAnalysis) {
          showSnackbar('Please select a job title', 'warning');
          return;
        }
        handlePrintAnalysis();
        setAnalysisDialogOpen(false);
      }}
      variant="contained"
      color="primary"
      disabled={analysisLoading || (analysisType === 'jobTitle' && !selectedJobTitleForAnalysis)}
    >
      {analysisLoading ? <CircularProgress size={20} /> : 'Generate Analysis'}
    </Button>
  </DialogActions>
</Dialog>


      {/* Add/Edit Availability Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Availability' : 'Add Availability'}
          <Typography variant="subtitle2" color="textSecondary">
            {selectedDay && formatDate(selectedDay.date)}
          </Typography>
          {(userInfo?.isManager || userInfo?.isAdmin) && selectedStaff && (
            <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
              For: {selectedStaff.name} ({selectedStaff.jobtitle})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Shift Presets */}
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WatchLater fontSize="small" />
                Quick Shift Presets
              </Typography>
              <Grid container spacing={1}>
                {Object.entries(SHIFT_PRESETS).map(([key, preset]) => (
                  <Grid item xs={6} sm={4} key={key}>
                    <Button
                      fullWidth
                      variant={selectedPreset === key ? "contained" : "outlined"}
                      size="small"
                      onClick={() => handlePresetChange(key)}
                      sx={{ 
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {preset.label}
                    </Button>
                  </Grid>
                ))}
              </Grid>
              {selectedPreset && (
                <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                  Selected: {SHIFT_PRESETS[selectedPreset].label}
                </Typography>
              )}
            </Box>
            
            {/* Multiple Day Selection */}
            <Box sx={{ mb: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday fontSize="small" />
                Apply to Multiple Days
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
    <Typography variant="caption" color="textSecondary" gutterBottom>
      Select days of the week to apply the same availability:
    </Typography>
    
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {Object.entries({
        monday: 'Mon',
        tuesday: 'Tue',
        wednesday: 'Wed',
        thursday: 'Thu',
        friday: 'Fri',
        saturday: 'Sat',
        sunday: 'Sun'
      }).map(([dayKey, dayLabel]) => {
        const isOriginalDay = dayKey === getDayNameFromDate(formData.startDateTime)?.toLowerCase();
        return (
          <Box key={dayKey} sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={selectedDays[dayKey]}
                  onChange={() => handleDayToggle(dayKey)}
                  disabled={isLoading}
                  sx={{
                    color: isOriginalDay ? 'primary.main' : undefined,
                    '&.Mui-checked': {
                      color: isOriginalDay ? 'primary.main' : undefined,
                    }
                  }}
                />
              }
              label={
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontWeight: isOriginalDay ? 'bold' : 'normal',
                    color: isOriginalDay ? 'primary.main' : 'text.primary'
                  }}
                >
                  {dayLabel}
                  {isOriginalDay && ' *'}
                </Typography>
              }
            />
          </Box>
        );
      })}
    </Box>
                
                {/* Display selected days info */}
                 {Object.values(selectedDays).some(day => day) && formData.startDateTime && (
      <Box sx={{ mt: 1, p: 1.5, bgcolor: 'white', borderRadius: 0.5 }}>
        <Typography variant="caption" fontWeight="medium" gutterBottom>
          This availability will be applied to:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
          {getSelectedDatesInfo().map(({ dayName, date, isOriginalDay }) => (
            <Chip
              key={`${dayName}-${date.toISOString()}`}
              label={`${dayName} ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}${isOriginalDay ? ' (Original)' : ''}`}
              size="small"
              color={isOriginalDay ? "primary" : "default"}
              variant={isOriginalDay ? "filled" : "outlined"}
              sx={{ 
                fontSize: '0.7rem',
                height: 24
              }}
            />
          ))}
        </Box>
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
          * Indicates the original day
        </Typography>
      </Box>
    
                )}
              </Box>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Time"
                  type="datetime-local"
                  name="startDateTime"
                  value={formData.startDateTime}
                  onChange={handleStartDateChange}
                  InputLabelProps={{ shrink: true }}
                  disabled={isLoading}
                  helperText="End time auto-sets to 8 hours later"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Time"
                  type="datetime-local"
                  name="endDateTime"
                  value={formData.endDateTime}
                  onChange={handleEndDateChange}
                  InputLabelProps={{ shrink: true }}
                  disabled={isLoading}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Category of Staff"
                  name="categoryOfStaff"
                  value={formData.categoryOfStaff}
                  onChange={handleFormChange}
                  disabled={isLoading}
                  helperText={
                    (userInfo?.isManager || userInfo?.isAdmin) && selectedStaff 
                      ? `Based on selected staff's job title: ${selectedStaff.jobtitle}`
                      : ''
                  }
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Ward/Department"
                  name="wardDepartmentArea"
                  value={formData.wardDepartmentArea}
                  onChange={handleFormChange}
                  disabled={isLoading}
                  helperText={
                    (userInfo?.isManager || userInfo?.isAdmin) && selectedStaff 
                      ? `Based on selected staff's ward: ${selectedStaff.ward}`
                      : ''
                  }
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Comments (Optional)"
                  name="comments"
                  value={formData.comments}
                  onChange={handleFormChange}
                  multiline
                  rows={3}
                  disabled={isLoading}
                  placeholder="Any additional notes about the availability..."
                  helperText={
                    (userInfo?.isManager || userInfo?.isAdmin) 
                      ? "These notes will be visible to the staff member"
                      : ""
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isUnavailable}
                      onChange={(e) => {
                        const isUnavailable = e.target.checked;
                        setFormData(prev => ({
                          ...prev,
                          isUnavailable: isUnavailable,
                          unavailabilityReason: isUnavailable ? prev.unavailabilityReason : '',
                          unavailabilityType: isUnavailable ? 'Full Day' : 'Full Day',
                          unavailabilityDetails: isUnavailable ? prev.unavailabilityDetails : '',
                          status: isUnavailable ? 'Approved' : 'Open'
                        }));
                      }}
                      color="error"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ 
                        width: 16, 
                        height: 16, 
                        bgcolor: 'error.light',
                        borderRadius: '2px',
                        border: '1px solid #d32f2f'
                      }} />
                      <Typography variant="body2" color={formData.isUnavailable ? 'error.main' : 'text.primary'}>
                        Mark as Unavailable
                      </Typography>
                      {formData.isUnavailable && (
                        <LocalHospital fontSize="small" color="error" />
                      )}
                    </Box>
                  }
                />
              </Grid>

              {formData.isUnavailable && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Unavailability Reason</InputLabel>
                      <Select
                        value={formData.unavailabilityReason}
                        label="Unavailability Reason"
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          unavailabilityReason: e.target.value
                        }))}
                      >
                        <MenuItem value="">Select Reason...</MenuItem>
                        {UNAVAILABILITY_REASONS.map((reason) => (
                          <MenuItem key={reason} value={reason}>
                            {reason}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Unavailability Type</InputLabel>
                      <Select
                        value={formData.unavailabilityType}
                        label="Unavailability Type"
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          unavailabilityType: e.target.value
                        }))}
                      >
                        <MenuItem value="Full Day">Full Day</MenuItem>
                        <MenuItem value="Half Day AM">Half Day (Morning)</MenuItem>
                        <MenuItem value="Half Day PM">Half Day (Afternoon)</MenuItem>
                        <MenuItem value="Partial Day">Partial Day</MenuItem>
                        <MenuItem value="Multi-Day">Multi-Day</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Unavailability Details (Optional)"
                      value={formData.unavailabilityDetails}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        unavailabilityDetails: e.target.value
                      }))}
                      multiline
                      rows={2}
                      placeholder="Additional details about the unavailability..."
                      size="small"
                    />
                  </Grid>

                     {/* ADD THIS NEW CHECKBOX FIELD */}
    <Grid item xs={12}>
      <FormControlLabel
        control={
          <Checkbox
            checked={formData.unavailablePayHours}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              unavailablePayHours: e.target.checked
            }))}
            color="primary"
          />
        }
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">
              Mark as paid hours
            </Typography>
            <Tooltip title="When checked, these hours will be counted for pay calculation">
              <Info fontSize="small" color="action" />
            </Tooltip>
          </Box>
        }
      />
      <Typography variant="caption" color="textSecondary" sx={{ ml: 4, display: 'block' }}>
        If checked, this unavailability period will be included for pay calculations
      </Typography>
    </Grid>      


                </>
              )}

              {formData.isSickLeave && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Sick Leave Type</InputLabel>
                      <Select
                        value={formData.sickLeaveType}
                        label="Sick Leave Type"
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          sickLeaveType: e.target.value
                        }))}
                      >
                        <MenuItem value="Full Day">Full Day</MenuItem>
                        <MenuItem value="Half Day AM">Half Day (Morning)</MenuItem>
                        <MenuItem value="Half Day PM">Half Day (Afternoon)</MenuItem>
                        <MenuItem value="Partial Day">Partial Day</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Sickness Reason (Optional)"
                      value={formData.sicknessReason}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        sicknessReason: e.target.value
                      }))}
                      placeholder="e.g., Flu, Injury, etc."
                      size="small"
                    />
                  </Grid>
                </>
              )}
              
              {/* Show existing availabilities for this day */}
              {selectedDay && getDayAvailabilities(selectedDay.date).length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Existing availabilities for this day:
                  </Typography>
                  <List dense>
                    {getDayAvailabilities(selectedDay.date)
                      .filter(a => !editMode || a._id !== editingAvailability?._id)
                      .map((availability) => (
                        <ListItem 
                          key={availability._id}
                          sx={{ 
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                            mb: 0.5
                          }}
                        >
                          <ListItemText
                            primary={`${formatTime(availability.startDateTime)} - ${formatTime(availability.endDateTime)}`}
                            secondary={
                              <span>
                                {formatHoursMinutes(availability.durationMinutes)} • {availability.status}
                                {availability.createdByManager && (
                                  <span style={{ marginLeft: '8px', fontStyle: 'italic', color: '#666' }}>
                                    (Added by manager)
                                  </span>
                                )}
                              </span>
                            }
                          />
                          <ListItemSecondaryAction>
                            <MuiIconButton
                              size="small"
                              onClick={(e) => handleEditClick(availability, e)}
                              disabled={isLoading}
                            >
                              <Edit fontSize="small" />
                            </MuiIconButton>
                            <MuiIconButton
                              size="small"
                              onClick={(e) => handleDeleteClick(availability._id, e)}
                              disabled={isLoading}
                              sx={{ ml: 0.5 }}
                            >
                              <Delete fontSize="small" />
                            </MuiIconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                  </List>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isLoading}>
            Cancel
          </Button>

          {editMode && editingAvailability && (
            <Button 
              onClick={handleDeleteConfirmOpen}
              variant="outlined" 
              color="error"
              disabled={isLoading}
              startIcon={<Delete />}
              sx={{ mr: 'auto' }}
            >
              Remove Availability
            </Button>
          )}

          <Button 
            onClick={handleFormSubmit} 
            variant="contained" 
            disabled={isLoading}
            startIcon={editMode ? <Edit /> : <Add />}
          >
            {editMode ? 'Update' : 'Add'} Availability
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default StaffAvailabilityView;